import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CircleSlash,
  ClipboardList,
  Gauge,
  Layers,
  Lock,
  RefreshCw,
  ShieldAlert,
  Tags,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { AdsStructureSection } from '@/components/AdsStructureSection';
import { formatDecimal, formatInteger } from '@/lib/language';
import {
  ADS_TIERS,
  adsErrorCode,
  labelUploadBlock,
  lastLabelRunFor,
  pilotIsDone,
  runAgeHours,
  tierApplyBlock,
  useAdsChangeLog,
  useAdsChangeLogAdd,
  useAdsDryRun,
  useAdsRotationPause,
  useAdsLabelPreview,
  useAdsLabelUpload,
  useAdsOverview,
  useAdsTierApply,
  type AdsChangeClassification,
  type AdsChangeLogEntry,
  type AdsDamping,
  type AdsDryRunResult,
  type AdsLabelFeed,
  type AdsLabelPreview,
  type AdsLastLabelRunState,
  type AdsOverview,
  type AdsOverviewAccount,
  type AdsPilotInfo,
  type AdsRunHead,
  type AdsRunStatus,
  type AdsTier,
  type AdsTransition,
  type LabelUploadBlock,
  type TierApplyBlock,
  useAdsGuardrails,
  guardrailWeeks,
  guardrailGeWeeks,
  guardrailGeThresholdPct,
  guardrailGeIsSelfControl,
  guardrailGtSeries,
  type AdsGuardrailCheck,
  type AdsGuardrailReport,
  type AdsGuardrailSeverity,
  type AdsGuardrailWeek,
  type AdsGuardrailGeWeek,
  type AdsGuardrailGtPoint,
} from '@/hooks/useAdsSteuerung';

/**
 * Ads-Steuerung, Scheibe 1 -- die Oberfläche hinter der nicht verlinkten Route
 * `/internal/ads-steuerung`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WARUM DIESE SEITE NIRGENDWO VERLINKT IST (harte Grenze G1)
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Dieselbe Begründung wie bei `/internal/ads-performance`: der Scope
 * `auth/adwords` ist bei Google als "sensitive" eingestuft, und Googles eigene
 * Formulierung lautet "If your app is already public, do not deploy unverified
 * scopes to your production traffic." Die Route ist per URL erreichbar und
 * steht weder in `AppSidebar.tsx` noch in `commandPaletteItems.ts`.
 * Gepinnt von `src/test/adsScopeIsolation.test.ts`.
 *
 * ⚠ KEIN `import.meta.env.DEV`-Gate. Das Demo-Video für die Scope-Freigabe
 * muss den Flow in der DEPLOYTEN App zeigen; ein DEV-Gate macht ihn unfilmbar.
 * Der Schutz ist die fehlende Verlinkung, nicht ein abgeschaltetes Feature.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * DEUTSCH, UND ZWAR FEST VERDRAHTET
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Der Vertrag legt Deutsch fest, weil diese Fläche ausschließlich das eigene
 * Team bedient. Sie bekommt deshalb bewusst KEINEN i18n-Namespace: der
 * Nachbar-Bildschirm `AdsPerformance` hat einen, weil dort Englisch die
 * Zwischenstufe zu einer späteren deutschen Fassung ist. Hier gibt es keine
 * zweite Fassung, und zwei Locale-Dateien mit identischem deutschem Inhalt
 * wären eine Behauptung über Übersetzbarkeit, die niemand einlösen wird.
 * Aus demselben Grund sind alle Zahlen fest in `de` formatiert -- ein Team, das
 * die App auf Englisch stehen hat, soll hier trotzdem "3.777" lesen.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * DIE REGELN SIND ZUSTÄNDE, KEINE HINWEISTEXTE
 * ═════════════════════════════════════════════════════════════════════════════
 *
 *   1. "Übernehmen" ist erst bedienbar, wenn ein Trockenlauf DIESES Kontos
 *      vorliegt und angezeigt wird. Der Lauf lebt in der Sitzung
 *      (`shownRuns`), nicht im Cache: nach einem Neuladen ist er weg, und dann
 *      muss erst wieder gerechnet und angesehen werden.
 *   2. Der Label-Knopf ist erst bedienbar, wenn ein ÜBERNOMMENER Tier-Lauf
 *      vorliegt.
 *   3. Nicht-Piloten bieten ihre Schreib-Knöpfe erst an, wenn der Pilot durch
 *      ist. Der Grund steht sichtbar an der Karte, nicht in einem Tooltip.
 *   4. Ein Konto mit veraltetem Ingest bietet keinen Label-Knopf an (G-D:
 *      blind wird nie rotiert).
 *   5. Es gibt nirgends einen Knopf, der eine Kampagne aktiviert oder pausiert.
 *
 * Jede dieser Regeln steht ZUSÄTZLICH serverseitig in `ads-tier-run` und in
 * den RPCs. Keine der beiden Seiten verlässt sich auf die andere: die
 * Oberfläche führt niemanden in eine Handlung, die dann abgelehnt wird, und
 * der Server bleibt gegen `curl` dicht. Die Sperrgründe selbst rechnet
 * `useAdsSteuerung.ts` (`tierApplyBlock`, `labelUploadBlock`), damit die Regel
 * einmal existiert und nicht einmal je Knopf.
 */

// ═════════════════════════════════════════════════════════════════════════════
// Formatierung
// ═════════════════════════════════════════════════════════════════════════════

/** Ganze Zahl, immer deutsch (siehe Kopf). */
function int(value: number | null | undefined): string {
  return formatInteger(value, 'de');
}

/** Dezimalzahl, immer deutsch. */
function dec(value: number | null | undefined, digits = 2): string {
  return formatDecimal(value, 'de', digits);
}

const NO_VALUE = '—';

function dateTime(iso: string | null | undefined): string {
  if (!iso) return NO_VALUE;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return NO_VALUE;
  return parsed.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

/** Reines Kalenderdatum (`YYYY-MM-DD`). Ohne die Uhrzeit-Ergänzung liest
 *  `new Date` UTC-Mitternacht und westlich von Greenwich steht der Vortag da. */
function dateOnly(iso: string | null | undefined): string {
  if (!iso) return NO_VALUE;
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return NO_VALUE;
  return parsed.toLocaleDateString('de-DE', { dateStyle: 'short' });
}

function ingestAgeText(hours: number | null): string {
  if (hours === null) return 'Ingest-Alter unbekannt';
  if (hours < 1) return 'Ingest frisch, unter einer Stunde';
  if (hours < 48) return `Ingest ${dec(hours, 1)} Stunden alt`;
  return `Ingest ${dec(hours, 1)} Stunden alt, Schwelle 48`;
}

// ═════════════════════════════════════════════════════════════════════════════
// Copy der Sperrgründe
// ═════════════════════════════════════════════════════════════════════════════

type AnyBlock = TierApplyBlock | LabelUploadBlock;

/** Vollständig ausgeschrieben statt mit Zusicherung: fällt in der Function
 *  ein neuer Sperrgrund an, verlangt der Compiler hier einen Satz dazu. */
const BLOCK_TEXT: Record<AnyBlock, string> = {
  no_account: 'Kein Konto gewählt.',
  negative_control: 'Negativkontrolle. Dieses Konto wird gelesen und nie beschrieben.',
  write_disabled: 'Schreiben ist für dieses Konto ausgeschaltet (write_enabled = false).',
  not_connected: 'Kein verbundenes Google-Ads-Konto. Ohne OAuth-Zeile läuft kein Lauf.',
  pilot_first: 'Pilotkonto zuerst.',
  no_dry_run: 'Erst einen Trockenlauf rechnen und ansehen.',
  run_already_applied: 'Dieser Lauf ist bereits übernommen.',
  dry_run_expired: 'Der Trockenlauf ist älter als 24 Stunden. Bitte neu rechnen.',
  ingest_stale: 'Ingest älter als 48 Stunden. Blind wird nie rotiert (G-D).',
  data_blackout:
    'Seit vier Tagen kein neuer Ads-Tag. Das Konto liefert nichts mehr aus -- blind wird nie '
    + 'rotiert (G-D). Nicht mit "Ingest veraltet" verwechseln: gezogen haben wir, es kommt nur '
    + 'nichts Neues an.',
  no_applied_run: 'Kein übernommener Tier-Lauf. Erst Trockenlauf rechnen und übernehmen.',
  no_preview: 'Vorschau noch nicht geladen.',
  no_free_slot: 'Kein freier Custom-Label-Slot. Fremde Werte werden nie überschrieben.',
  no_tier: 'Kein Produkt dieses Feeds trägt ein gerechnetes Tier.',
  mc_access: 'Zugriff auf das Merchant Center ist entzogen.',
  nothing_eligible: '0 labelbar: kein Produkt ist optimiert UND freigegeben.',
  logic_frozen:
    'Sperrfenster 22.09. bis 19.10.: die Tier-Logik dieses Laufs weicht von der übernommenen ab. '
    + 'Eine Änderung während des Messfensters ist ein dokumentierter Abbruch der Messung. '
    + 'Das gilt seit dem 23.08.2026 auch für die Fensterlänge: 14 oder 56 Tage statt 28 rechnen '
    + 'andere Tiers und sind damit eine Logikänderung.',
  rotation_moratorium:
    'Rotations-Moratorium 01.09. bis 13.09.: nach dem Cutover bleiben die Labels knapp zwei Wochen stehen, '
    + 'weil jede Änderung die Lernphase der Kampagnen zurücksetzt. Rechnen und Übernehmen laufen '
    + 'weiter - nur der Upload zu Google ist gesperrt. Erste echte Rotation ist der 14.09.',
  experiment_running:
    'Auf einem Feed dieses Kontos läuft ein A/B-Experiment. Ein Tier- oder Label-Schreibzugriff '
    + 'würde dessen Messung zerstören, deshalb ist beides gesperrt, bis das Experiment beendet ist.',
};

function blockText(block: AnyBlock, pilotLabel: string | null): string {
  if (block === 'pilot_first') {
    return pilotLabel
      ? `Pilotkonto zuerst. Erst wenn ${pilotLabel} einen übernommenen Lauf hat.`
      : 'Pilotkonto zuerst.';
  }
  return BLOCK_TEXT[block] ?? 'Gesperrt.';
}

/**
 * Fehlercodes der Edge Function. Jeder erwartete 409 des Vertrags hat hier
 * einen Satz -- ein "Edge Function returned a non-2xx status code" wäre für
 * jeden dieser sechs Fälle dieselbe nichtssagende Meldung.
 */
const ERROR_TEXT: Record<string, string> = {
  WRITE_DISABLED: 'Das Konto steht mit write_enabled = false in der Rollout-Liste.',
  PILOT_FIRST: 'Der Pilot ist noch nicht durch. Erst Spielgigant übernehmen.',
  LOGIC_FROZEN:
    'Sperrfenster: während des Messfensters dürfen weder Schwellen noch logic_version abweichen.',
  INGEST_STALE: 'Ingest älter als 48 Stunden (G-D). Erst die Daten nachziehen.',
  DATA_BLACKOUT:
    'Seit vier Tagen kommt kein neuer Ads-Tag mehr an (G-D). Anders als bei INGEST_STALE liegt '
    + 'das nicht am Ziehen: gezogen wird nächtlich, das Konto liefert nur nichts mehr aus.',
  NO_APPLIED_RUN: 'Es gibt keinen übernommenen Tier-Lauf für dieses Konto.',
  ROTATION_MORATORIUM:
    'Rotations-Moratorium 01.09. bis 13.09.: in diesem Fenster wird kein Label zu Google '
    + 'hochgeladen. Erste echte Rotation ist der 14.09.',
  EXPERIMENT_RUNNING:
    'Auf einem Feed dieses Kontos läuft ein A/B-Experiment. Gesperrt, bis es beendet ist.',
  RUN_NOT_FOUND: 'Der Lauf gehört nicht zu diesem Konto oder existiert nicht mehr.',
  RUN_NOT_DRY_RUN: 'Der Lauf ist kein Trockenlauf mehr.',
  RUN_EXPIRED: 'Der Trockenlauf ist älter als 24 Stunden. Bitte neu rechnen.',
  UNKNOWN_ACCOUNT: 'Dieses Ads-Konto ist nicht bekannt.',
  FORBIDDEN: 'Dieses Ads-Konto gehört nicht zu diesem Zugang.',
  EMPTY_RESPONSE: 'Die Function hat nichts zurückgegeben.',
  DRY_RUN_MALFORMED: 'Die Antwort des Trockenlaufs war unlesbar.',
  // Der häufigste echte Fehler: die Rechnung selbst ist gescheitert. Bis zum
  // 22.08. war das auf großen Konten ein `statement_timeout` — der
  // Feed-Scope kam als CTE aus einer Set-Returning-Function, der Planer
  // schätzte sie auf 1000 Zeilen und las die halbe Produkttabelle.
  DRY_RUN_FAILED:
    'Die Rechnung ist gescheitert. Bei sehr großen Konten kann sie die Zeitgrenze der '
    + 'Datenbank reißen; dann hilft ein kürzeres Fenster oder der Hinweis an die Technik.',
  APPLY_REJECTED:
    'Die Übernahme wurde von der Datenbank abgelehnt. Meist ist der Trockenlauf zu alt, '
    + 'schon übernommen oder er trifft keine Produkte mehr. Bitte neu rechnen.',
  // Die drei Codes der Teilschritt-Schleife (seit 23.08.). Rechnen und
  // Übernehmen laufen nicht mehr als je ein Statement, sondern als Folge von
  // Durchgängen; entsprechend gibt es "mittendrin abgebrochen" jetzt als
  // eigenen Ausgang. Was hier steht, ist deshalb vor allem die Antwort auf die
  // Frage, die ein Abbruch sofort aufwirft: ist etwas halb geschrieben, und
  // was muss ich jetzt tun?
  DRY_RUN_INCOMPLETE:
    'Die Rechnung wurde nach dem Zeitbudget abgebrochen und ist unvollständig. Es wurde nichts '
    + 'übernommen, und dieser Lauf lässt sich auch nicht übernehmen. Einfach neu rechnen.',
  // Beide Codes hinterlassen DENSELBEN Zustand -- Lauf auf `applying`, Cursor
  // am letzten geschriebenen Chunk -- und bekommen deshalb denselben Rat.
  // APPLY_FAILED riet bis zum 23.08. zu "neu rechnen": das ist der Rat, der
  // den angefangenen Lauf überholt, statt ihn zu Ende zu bringen, und er
  // kostet auf dem größten Konto einen weiteren vollen Rechenlauf.
  // Fortsetzen kann beides, denn `ads_tier_apply_begin` nimmt einen Lauf auf
  // `applying` wieder an. Das gilt auch für den zweiten APPLY_FAILED-Pfad,
  // bei dem erst das Abschließen scheitert: dann sind null Produkte offen,
  // und der erneute Aufruf läuft direkt in `ads_tier_apply_finalize`.
  APPLY_FAILED:
    'Die Übernahme ist mitten im Schreiben gescheitert. Ein Teil der Produkte kann bereits das '
    + 'neue Tier tragen; an Google ging nichts. Der Lauf steht auf "Übernahme unvollständig", '
    + 'erneut auf "Übernehmen" klicken setzt dort fort, wo abgebrochen wurde.',
  APPLY_INCOMPLETE:
    'Die Übernahme wurde nach dem Zeitbudget abgebrochen. Ein Teil der Produkte trägt schon das '
    + 'neue Tier, der Lauf gilt aber NICHT als übernommen und der Label-Knopf bleibt zu. Er steht '
    + 'auf "Übernahme unvollständig", erneut auf "Übernehmen" klicken setzt dort fort, wo '
    + 'abgebrochen wurde.',
  ACCOUNT_NOT_AVAILABLE:
    'Dieses Ads-Konto gehört nicht zu diesem Zugang. Die vier Rollout-Konten hängen an '
    + 'google@oma.ag.',
  NOT_ENROLLED: 'Dieses Ads-Konto ist nicht in den Rollout eingeschrieben.',
  BLOCKED: 'Eine Vorbedingung ist nicht erfüllt. Der Grund steht an der Kontokarte.',
  FEED_BLOCKED: 'Dieser Feed ist für einen Label-Lauf gesperrt. Der Grund steht in der Zeile.',
  FEED_NOT_IN_SCOPE:
    'Dieser Feed gehört nicht zum Scope des Kontos. Zum Scope zählt, was an der verlinkten '
    + 'Merchant Center hängt UND für dieses Ads-Konto jemals Leistungszeilen hatte.',
  UPLOAD_ENQUEUE_FAILED:
    'Der Upload konnte nicht eingereiht werden. Es wurde nichts an Google geschickt.',
  CHANGE_LOG_ADD_FAILED: 'Der Eintrag konnte nicht gespeichert werden.',
  DATABASE_ERROR: 'Die Datenbank hat nicht geantwortet. Bitte erneut versuchen.',
  LOG_ACTION_REQUIRED: 'Die Handlung braucht eine Kennung, z.B. budget_change.',
  SUMMARY_REQUIRED: 'Ohne Zusammenfassung ist der Eintrag kein Nachweis.',
  INVALID_CLASSIFICATION: 'Unbekannte Einstufung.',
  ADS_CUSTOMER_ID_REQUIRED: 'Es wurde kein Konto mitgeschickt.',
  FEED_ID_REQUIRED: 'Es wurde kein Feed mitgeschickt.',
  RUN_ID_REQUIRED: 'Es wurde kein Lauf mitgeschickt.',
  UNAUTHORIZED: 'Nicht angemeldet, oder die Sitzung ist abgelaufen.',
  UNKNOWN_ACTION: 'Unbekannte Aktion. Das ist ein Fehler der Oberfläche, nicht deiner.',
  RESERVED_LOG_ACTION:
    'Diese Kennung schreibt die Software selbst (tier_apply, label_upload, set_budget, set_troas, '
    + 'set_cpc_ceiling, campaign_pause -- der Abschnitt Kampagnenstruktur schreibt sein Protokoll '
    + 'darüber). Für eine Handarbeit an derselben Sache eine eigene wählen, z.B. '
    + 'tier_apply_manuell -- sonst wäre im Protokoll nicht mehr zu unterscheiden, wer gehandelt hat.',
  // Die zwei Codes des Veto-Schalters (Wochenlauf, seit 24.08.).
  ROTATION_FLAG_REQUIRED:
    'Der Schalter braucht einen eindeutigen Zielzustand (pausiert ja/nein). Fehler der '
    + 'Oberfläche, nicht deiner.',
  NOT_ROTATABLE:
    'Dieses Konto rotiert nie (kein Schreibkonto) -- es gibt nichts zu pausieren.',
  NOT_A_ROLLOUT_OPERATOR:
    'Dieser Zugang bedient den Rollout nicht. Die Fläche steht nur Nutzern offen, denen mindestens '
    + 'eines der eingeschriebenen Ads-Konten gehört.',
};

function errorText(error: unknown): string {
  const code = adsErrorCode(error);
  if (code) return ERROR_TEXT[code] ?? `Die Function hat abgelehnt: ${code}`;
  if (error instanceof Error && error.message) return error.message;
  return 'Unbekannter Fehler.';
}

// ═════════════════════════════════════════════════════════════════════════════
// Kleinteile
// ═════════════════════════════════════════════════════════════════════════════

const TIER_PILL: Record<AdsTier, string> = {
  hero: 'bg-success-light text-success-ink',
  mid: 'bg-primary-light text-primary-ink',
  drain: 'bg-destructive-light text-destructive-ink',
  zombie: 'bg-muted text-muted-foreground',
};

const TIER_BAR: Record<AdsTier, string> = {
  hero: 'bg-success',
  mid: 'bg-primary',
  drain: 'bg-destructive',
  zombie: 'bg-muted-foreground/40',
};

const DAMPING_TEXT: Record<AdsDamping, string> = {
  immediate: 'sofort, Ausnahme',
  confirmed: 'bestätigt, 2. Lauf',
  pending: 'erstmals, gedämpft',
  unchanged: 'unverändert',
};

const DAMPING_TONE: Record<AdsDamping, string> = {
  immediate: 'text-success-ink',
  confirmed: 'text-success-ink',
  pending: 'text-muted-foreground',
  unchanged: 'text-muted-foreground',
};

/**
 * Der Zustand eines Laufkopfs als Anzeigename -- alle fünf Werte
 * ausgeschrieben, wie bei BLOCK_TEXT und aus demselben Grund.
 *
 * Bis zum 23.08. kannte die Fläche drei Namen und schob alles übrige in ein
 * "Trockenlauf". Seit Rechnen und Übernehmen in Teilschritten laufen, gibt es
 * aber `applying`, und das ist der EINZIGE Zustand, in dem `products` und
 * `ads_tier_runs` dauerhaft auseinanderlaufen können: ein Teil der Produkte
 * trägt bereits sein neues Tier, der Lauf gilt trotzdem nicht als übernommen.
 * Genau dieser Fall stand als der harmloseste von allen da.
 *
 * Deshalb ein `Record` statt einer Kette von Fragezeichen: ein sechster
 * Zustand verlangt hier einen Namen, statt sich still in den letzten
 * `else`-Zweig zu setzen. `computing` erreicht die Tafel heute nicht
 * (`loadLatestRun` in der Function filtert es heraus, weil dessen Zähler noch
 * auf null stehen) und bekommt trotzdem einen -- "Trockenlauf, 0 Wechsel"
 * wäre die falscheste der möglichen Auskünfte.
 */
const RUN_STATUS: Record<AdsRunStatus, { text: string; tone: string }> = {
  computing: { text: 'wird gerechnet', tone: '' },
  dry_run: { text: 'Trockenlauf', tone: '' },
  applying: { text: 'Übernahme unvollständig', tone: 'font-semibold text-warning-ink' },
  applied: { text: 'übernommen', tone: '' },
  superseded: { text: 'überholt', tone: '' },
};

/** Kurznotiz zu den vier bekannten Begründungs-Codes. Unbekannte Codes stehen
 *  ohne Notiz da -- lieber keine Erklärung als eine erfundene. */
const REASON_NOTE: Record<string, string> = {
  kein_volumen: 'hält in zombie',
  preis_ueber_benchmark: 'bei drain',
  zu_wenig_daten: 'hält in mid',
  content_schwach: 'bei drain',
};

function TierPill({ tier, count }: { tier: AdsTier; count?: number }) {
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold tabular-nums ${TIER_PILL[tier]}`}
    >
      {tier}
      {count !== undefined && <span className="ml-1">{int(count)}</span>}
    </span>
  );
}

/** Verteilung als ein Balken. Bei null Produkten bleibt er leer statt zu
 *  behaupten, alles sei zombie. */
function TierBar({ tiers }: { tiers: Record<AdsTier, number> }) {
  const total = ADS_TIERS.reduce((sum, tier) => sum + tiers[tier], 0);
  return (
    <div
      className="flex h-6 gap-0.5 overflow-hidden rounded-md ring-1 ring-border"
      role="img"
      aria-label={ADS_TIERS.map((tier) => `${tier} ${int(tiers[tier])}`).join(', ')}
    >
      {total === 0 ? (
        <div className="w-full bg-muted" />
      ) : (
        ADS_TIERS.map((tier) => (
          <div
            key={tier}
            className={TIER_BAR[tier]}
            style={{ width: `${(tiers[tier] / total) * 100}%` }}
          />
        ))
      )}
    </div>
  );
}

function BlockNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
      <Lock className="mt-0.5 h-3 w-3 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

function WarnBanner({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning-light p-4">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-ink" />
      <div className="space-y-1">
        <p className="text-sm font-semibold text-warning-ink">{title}</p>
        {children && <div className="text-sm text-warning-ink">{children}</div>}
      </div>
    </div>
  );
}

/**
 * Der Kasten zum Laufzustand `applying` -- eine halb geschriebene Übernahme.
 *
 * Er steht an ZWEI Stellen (Kontokarte in Abschnitt 1, Lesekontext-Karte in
 * Abschnitt 2) und ist deshalb eine eigene Komponente: das sind die beiden
 * Flächen, an denen ein Bediener diesen Lauf antrifft, und zwei getrennt
 * gepflegte Formulierungen desselben Zustands driften auseinander.
 *
 * Optisch derselbe Kasten wie das gerissene G-D-Guardrail direkt darüber. Das
 * ist Absicht: die Seite hat genau eine Darstellung für "hier muss ein Mensch
 * hinsehen", und ein dritter Ton für den dritten Warnfall wäre nur noch Lärm.
 *
 * Zwei Aussagen tragen den Text, und beide sind nachgerechnet, nicht geraten:
 * an Google ging nichts (der Label-Lauf ist ein eigener Schritt und hängt an
 * einem ÜBERNOMMENEN Lauf, den es hier gerade nicht gibt), und ein erneuter
 * Klick setzt fort statt neu zu schreiben (`ads_tier_apply_begin` nimmt einen
 * Lauf auf `applying` wieder an, `apply_cursor` sagt wo). Der Nachsatz zum
 * Neuladen ist keine Vorsicht, sondern der Sitzungsvorbehalt aus harter Grenze
 * 3: "Übernehmen" hängt an `shownRun`, und der ist nach einem Neuladen weg.
 *
 * Der Text sagt "abgebrochen" und hedgt nicht auf "abgebrochen oder läuft
 * gerade". `applying` heißt streng genommen beides, aber die laufende
 * Übernahme steckt in EINEM blockierenden Request, und die Übersicht wird
 * währenddessen nicht neu geholt (`useAdsTierApply` invalidiert erst
 * `onSuccess`, und dann steht der Lauf auf `applied`). Diesen Kasten sieht
 * also nur, wer die Seite nach dem Abbruch neu lädt -- oder in genau den
 * Sekunden, in denen ein zweiter Bediener übernimmt. Für diesen Rand einen
 * unbestimmten Satz zu schreiben, hieße den Regelfall unschärfer zu machen.
 */
function InterruptedApplyNote({ run }: { run: AdsRunHead }) {
  // "Ein Teil" ist die ehrliche Auskunft, solange wir die Zahl nicht haben --
  // und eine Zahl ist hier weit mehr wert als ein Wort: sie sagt, ob der
  // Abbruch nach dem ersten oder nach dem vorletzten Durchgang kam, und damit,
  // wie viel ein Fortsetzen noch kostet. `apply_updated` ist beim Schreiben
  // mitgeführt, also die tatsächlich geschriebene Menge; `n_changes` ist die
  // Gesamtmenge des Laufs.
  //
  // Beide Bedingungen sind nötig: `null` heißt "die Function hat das Feld nicht
  // geliefert" (alte Fassung), und 0 heißt "noch kein einziger Durchgang durch"
  // -- dann trägt kein Produkt das neue Tier, und "N von M" wäre irreführend
  // präzise für einen Zustand, in dem nichts passiert ist.
  const geschrieben = run.apply_updated;
  const mitZahl = geschrieben !== null && geschrieben > 0;

  return (
    <div className="flex items-start gap-2 rounded-md bg-warning-light p-2.5">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-ink" />
      <p className="text-xs font-medium text-warning-ink">
        Die Übernahme wurde mittendrin abgebrochen.{' '}
        {mitZahl ? (
          <>
            <span className="tabular-nums">{int(geschrieben)}</span> von{' '}
            <span className="tabular-nums">{int(run.n_changes)}</span> Produkten tragen bereits das
            neue Tier
          </>
        ) : (
          <>Ein Teil der Produkte trägt bereits das neue Tier</>
        )}
        , der Lauf gilt aber nicht als übernommen und der Label-Knopf bleibt zu. An Google ging
        nichts. Erneut auf "Übernehmen" klicken setzt dort fort, wo abgebrochen wurde, solange der
        Lauf in dieser Sitzung angezeigt wird. Nach einem Neuladen hilft nur noch rechnen und
        übernehmen; der nächste Lauf rechnet ohnehin alles frisch.
      </p>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Abschnitt 1 -- Rollout-Tafel (Artboard 1)
// ═════════════════════════════════════════════════════════════════════════════

interface AccountCardProps {
  account: AdsOverviewAccount;
  pilot: AdsPilotInfo | null;
  isSelected: boolean;
  isBusy: boolean;
  rotationBusy: boolean;
  onSelect: (customerId: string) => void;
  onDryRun: (customerId: string) => void;
  onRotationPause: (customerId: string, paused: boolean) => void;
}

function AccountCard({
  account, pilot, isSelected, isBusy, rotationBusy, onSelect, onDryRun, onRotationPause,
}: AccountCardProps) {
  const isControl = account.role === 'negative_control';
  const pilotLabel = pilot?.label ?? null;
  const blockedForWrites = !isControl && !pilotIsDone(account, pilot);
  // Jeder dieser vier Gründe hat auf der Karte bereits seine eigene Zeile
  // (Kontrolle, Pilot, write_enabled, G-D-Kasten). Übrig bleibt nur, was die
  // Function meldet und wir nicht selbst herleiten.
  const ownNoteShown =
    isControl || blockedForWrites || !account.write_enabled || account.ingest_stale;
  const serverOnlyBlock = ownNoteShown ? null : account.blocked_reason;

  return (
    <Card
      data-testid={`ads-account-card-${account.ads_customer_id}`}
      className={isSelected ? 'border-primary' : undefined}
    >
      <CardContent className="space-y-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-base font-semibold">{account.label}</span>
              {account.is_pilot && (
                <span className="rounded-md bg-primary-light px-2 py-0.5 text-[11px] font-semibold text-primary-ink">
                  Pilot
                </span>
              )}
              {isControl && (
                <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                  Negativkontrolle
                </span>
              )}
            </div>
            <p className="font-mono text-xs text-muted-foreground tabular-nums">
              {account.ads_customer_id}
              {account.currency ? ` · ${account.currency}` : ''}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-xl font-bold tabular-nums">{dec(account.roas_target, 2)}</div>
            {/* E3: das Kontoziel ist der Ist-ROAS über das Fenster und wird bei
                jedem Lauf neu abgeleitet. Es gibt hier bewusst kein Eingabefeld. */}
            <div className="text-[11px] text-muted-foreground">Kontoziel ROAS, abgeleitet</div>
          </div>
        </div>

        <TierBar tiers={account.tiers} />
        <div className="flex flex-wrap gap-1.5">
          {ADS_TIERS.map((tier) => (
            <TierPill key={tier} tier={tier} count={account.tiers[tier]} />
          ))}
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {int(account.feeds_in_scope)} {account.feeds_in_scope === 1 ? 'Feed' : 'Feeds'}
            {' · '}
            {int(account.products_in_scope)} Produkte
          </span>
          <span className="text-right tabular-nums">Daten bis {dateOnly(account.data_through)}</span>
          {/* Serving-Gate: die Kennzeichnung, die nicht-auslieferbare Produkte
              sichtbar macht, statt sie unmarkiert mitzuzaehlen. 0 wird nicht
              gerendert -- eine Null-Zeile auf jeder Karte waere Rauschen. */}
          {account.products_disapproved > 0 && (
            <span className="col-span-2 tabular-nums text-warning-ink">
              davon {int(account.products_disapproved)} von Google für Shopping Ads abgelehnt
              {' '}(nicht auslieferbar, ausgenommen von der Label-Rotation)
            </span>
          )}
        </div>

        {account.ingest_stale || account.data_blackout ? (
          <div className="flex items-start gap-2 rounded-md bg-warning-light p-2.5">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-ink" />
            <p className="text-xs font-medium text-warning-ink">
              {/*
                Beide Hälften von G-D, aber nie derselbe Satz: der eine Zustand
                heißt "wir haben lange nicht gezogen", der andere "das Konto
                liefert seit Tagen nichts". Wer das eine für das andere hält,
                repariert den Ingest, während das Konto stillsteht.
              */}
              {account.ingest_stale
                ? `G-D gerissen: ${ingestAgeText(account.ingest_age_hours)}.`
                : `G-D gerissen: jüngster Ads-Tag ${dateOnly(account.data_through)}, seit `
                  + `${account.data_age_days ?? '?'} Tagen kommt nichts Neues.`}
              {' '}Kein Label-Lauf, blind wird nie rotiert.
            </p>
          </div>
        ) : (
          <p className="flex items-center gap-1.5 text-xs text-success-ink">
            <Check className="h-3.5 w-3.5" />
            {ingestAgeText(account.ingest_age_hours)}
          </p>
        )}

        {account.latest_run ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground tabular-nums">
              Letzter Lauf {dateTime(account.latest_run.created_at)}
              {' · '}
              <span className={RUN_STATUS[account.latest_run.status].tone}>
                {RUN_STATUS[account.latest_run.status].text}
              </span>
              {' · '}
              {int(account.latest_run.n_changes)} Wechsel
            </p>
            {/* Der eine Zustand, der einen Menschen braucht, und der einzige
                Ort, an dem er ohne Zutun auffällt: die Tafel steht nach jedem
                Neuladen da, der Abbruch selbst ist dann längst vorbei. Als
                Zeile im Fließtext ginge er zwischen Ingest-Alter und
                Sperrgründen unter -- eine halb geschriebene Übernahme ist
                nichts, was man beim Überfliegen findet. */}
            {account.latest_run.status === 'applying' && <InterruptedApplyNote run={account.latest_run} />}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Noch kein Lauf gerechnet.</p>
        )}

        {!account.connected && (
          <BlockNote>Kein verbundenes Ads-Konto. Ohne OAuth-Zeile läuft kein Lauf.</BlockNote>
        )}

        {/* golfimport: sichtbar in der Tafel, ohne jeden Schreib-Knopf. Die
            Karte sagt warum -- eine Karte ohne Knöpfe und ohne Grund liest
            sich als Fehler der Software.

            "Auswählen" ist KEIN Schreib-Knopf und gehört deshalb auch hier hin.
            Bis zum 23.08.2026 fiel er mit weg, weil er im selben Block stand wie
            "Trockenlauf rechnen" -- und damit war die Guardrail-Tafel der
            Negativkontrolle in der Oberfläche UNERREICHBAR. Genau ihre Befunde
            sind aber das, was der Messplan sehen will: der Wert des Kontos
            besteht darin, dass an ihm nichts passiert, und das muss man
            nachsehen können. Die harte Grenze lautet "Lesen ja, schreiben nie",
            nicht "gar nichts". Aufgefallen im Prod-E2E: von fünf Konten trugen
            nur vier einen Auswahl-Knopf. */}
        {isControl ? (
          <div className="space-y-2">
            <BlockNote>
              {blockText('negative_control', pilotLabel)} Sie liefert über dieselbe Pipeline
              durchgehend Ads-Daten und beantwortet die Gegenfrage, ob sich ein vergleichbares
              Konto im selben Zeitraum ohne unser Zutun genauso bewegt.
            </BlockNote>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onSelect(account.ads_customer_id)}
              disabled={isSelected}
            >
              {isSelected ? 'ausgewählt' : 'auswählen'}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {blockedForWrites && <BlockNote>{blockText('pilot_first', pilotLabel)}</BlockNote>}
            {!account.write_enabled && <BlockNote>{blockText('write_disabled', pilotLabel)}</BlockNote>}
            {/* Was die Function sperrt, wir aber selbst nicht hergeleitet
                haben. Die vier heute möglichen Gründe haben jeder ihre eigene
                Darstellung; käme ein fünfter dazu, stünde er sonst nirgends
                und die Karte sähe grundlos knopflos aus. */}
            {serverOnlyBlock && <BlockNote>{blockText(serverOnlyBlock, pilotLabel)}</BlockNote>}
            {/* Wochenlauf (24.08.): Veto-Schalter + stehende Ankündigung.
                Der Schalter pausiert NUR die Montag-15:30-Upload-Phase;
                Rechnen und Übernehmen laufen weiter, damit die
                Dämpfungskette nicht abreißt. Eine beim Pausieren stehende
                Ankündigung wird verworfen, nicht nachgeholt. Nur auf
                Schreibkonten gerendert -- die Function lehnt alles andere
                mit NOT_ROTATABLE ab, und ein Schalter, der immer 409
                liefert, wäre eine Attrappe. */}
            {account.write_enabled && (
              <div className="space-y-1.5 rounded-md border border-border p-2.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium">Wochenlauf-Rotation</p>
                    <p className="text-[11px] text-muted-foreground">
                      {account.rotation_paused
                        ? 'Pausiert: montags wird gerechnet, aber kein Label hochgeladen.'
                        : 'Aktiv: Montag 09:00 rechnen, 15:30 Upload nach 6 h Veto-Fenster.'}
                    </p>
                  </div>
                  <Switch
                    aria-label={`Wochenlauf-Rotation für ${account.label}`}
                    checked={!account.rotation_paused}
                    disabled={rotationBusy}
                    onCheckedChange={(checked) =>
                      onRotationPause(account.ads_customer_id, !checked)
                    }
                  />
                </div>
                {account.rotation_upload_due_at && (
                  <p className="text-[11px] tabular-nums text-warning-ink">
                    Label-Upload angekündigt, fällig {dateTime(account.rotation_upload_due_at)}.
                    {' '}Veto: Schalter aus, dann wird diese Ankündigung verworfen.
                  </p>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={isSelected ? 'default' : 'outline'}
                disabled={isBusy || !account.connected}
                onClick={() => onDryRun(account.ads_customer_id)}
              >
                Trockenlauf rechnen
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onSelect(account.ads_customer_id)}
                disabled={isSelected}
              >
                {isSelected ? 'ausgewählt' : 'auswählen'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RolloutBoard({
  overview,
  selectedCustomerId,
  isBusy,
  rotationBusy,
  onSelect,
  onDryRun,
  onRotationPause,
}: {
  overview: AdsOverview;
  selectedCustomerId: string | null;
  isBusy: boolean;
  rotationBusy: boolean;
  onSelect: (customerId: string) => void;
  onDryRun: (customerId: string) => void;
  onRotationPause: (customerId: string, paused: boolean) => void;
}) {
  // Rollout-Konten zuerst, Pilot ganz vorn, die Negativkontrolle zuletzt --
  // sie gehört in die Tafel, aber nicht an ihren Anfang.
  const ordered = useMemo(() => {
    const rank = (account: AdsOverviewAccount) =>
      account.role === 'negative_control' ? 2 : account.is_pilot ? 0 : 1;
    return [...overview.accounts].sort(
      (a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label, 'de'),
    );
  }, [overview.accounts]);

  return (
    <section className="space-y-3" aria-labelledby="ads-rollout-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="ads-rollout-heading" className="text-lg font-semibold">
          Rollout-Tafel
        </h2>
        <span className="text-xs text-muted-foreground tabular-nums">
          {int(ordered.filter((a) => a.role === 'rollout').length)} Rollout-Konten
          {' · '}
          {int(ordered.filter((a) => a.role === 'negative_control').length)} Negativkontrolle
        </span>
      </div>

      {ordered.length === 0 ? (
        <EmptyState
          icon={Gauge}
          title="Keine Konten in der Rollout-Liste"
          description="ads_rollout_accounts ist leer oder keines der Konten gehört zu diesem Zugang."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {ordered.map((account) => (
            <AccountCard
              key={account.ads_customer_id}
              account={account}
              pilot={overview.pilot}
              isSelected={account.ads_customer_id === selectedCustomerId}
              isBusy={isBusy}
              rotationBusy={rotationBusy}
              onSelect={onSelect}
              onDryRun={onDryRun}
              onRotationPause={onRotationPause}
            />
          ))}
        </div>
      )}

      <p className="text-xs leading-relaxed text-muted-foreground">
        Das Kontoziel ist kein gesetzter Wert, sondern der Ist-ROAS des Kontos über das Fenster. Es
        wird bei jedem Rechenlauf neu abgeleitet. Tier-Regeln: hero heißt Conversions über null und
        ROAS mindestens am Kontoziel, drain heißt Kosten ab der Schwelle bei null Conversions oder
        ROAS unter der Hälfte des Ziels, mid ist der Rest mit Ads-Daten, zombie hat im Fenster gar
        keine Ads-Daten.
      </p>
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Ablaufleiste -- macht die beiden harten Grenzen sichtbar
// ═════════════════════════════════════════════════════════════════════════════

function StepStrip({
  account,
  shownRun,
  applyBlocked,
  labelBlocked,
  pilotLabel,
}: {
  account: AdsOverviewAccount;
  shownRun: AdsRunHead | null;
  applyBlocked: TierApplyBlock | null;
  labelBlocked: LabelUploadBlock | null;
  pilotLabel: string | null;
}) {
  const step1Done = !!shownRun;
  const step2Done = shownRun?.status === 'applied' || account.latest_run?.status === 'applied';

  const steps: Array<{ title: string; body: string; done: boolean; blocked: string | null }> = [
    {
      title: '1 · Tiering rechnen',
      body: shownRun
        ? `${int(shownRun.n_changes)} Produkte mit neuem Tier, Fenster ${dateOnly(shownRun.window_start)} bis ${dateOnly(shownRun.window_end)}.`
        : 'Noch kein Trockenlauf in dieser Sitzung.',
      done: step1Done,
      blocked: account.connected ? null : blockText('not_connected', pilotLabel),
    },
    {
      title: '2 · Wechsel übernehmen',
      body: shownRun
        ? `${int(shownRun.n_immediate + shownRun.n_confirmed)} gehen jetzt ins Tier, ${int(shownRun.n_pending)} bleiben gedämpft.`
        : 'Schreibt das Tier in die Datenbank, nicht in den Feed.',
      done: !!step2Done,
      blocked: applyBlocked ? blockText(applyBlocked, pilotLabel) : null,
    },
    {
      title: '3 · Labels hochladen',
      body: 'Erst hier verlässt etwas das Haus: der Supplemental Feed bei Google.',
      done: false,
      blocked: labelBlocked ? blockText(labelBlocked, pilotLabel) : null,
    },
  ];

  return (
    <div className="grid gap-3 md:grid-cols-3">
      {steps.map((step) => (
        <div
          key={step.title}
          className={`rounded-lg border p-4 ${
            step.done ? 'border-success/40 bg-success-light/40' : 'border-border'
          }`}
        >
          <div className="flex items-center gap-2">
            {step.done ? (
              <Check className="h-4 w-4 text-success-ink" />
            ) : step.blocked ? (
              <Lock className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ArrowRight className="h-4 w-4 text-primary" />
            )}
            <span className="text-sm font-semibold">{step.title}</span>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{step.body}</p>
          {step.blocked && <p className="mt-1.5 text-xs text-muted-foreground">{step.blocked}</p>}
        </div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Guardrail-Tafel (Artboard 4)
// ═════════════════════════════════════════════════════════════════════════════
//
// Zeigt den PROTOKOLLIERTEN Befund, nicht eine frische Rechnung. Der tägliche
// Lauf schreibt je Guardrail und Konto eine Zeile, auch wenn nichts auffällt --
// der vorregistrierte Messplan verlangt die Werte immer, auch bei null. Würde
// diese Fläche beim Öffnen neu rechnen, zeigte sie etwas anderes als das, was
// im Protokoll steht, und die Frage "was stand gestern da" wäre nicht mehr
// beantwortbar.

/**
 * Überschrift, Regel und Reaktion je Guardrail. Wortlaut aus Messplan §6.
 *
 * `unit` ist OPTIONAL und darf nur dort stehen, wo der Wert IMMER dieselbe
 * Einheit trägt. G-D hat bewusst keine: seine Zahl ist mal ein Alter in
 * Stunden (Zeitpunkt des letzten Pulls) und mal eins in Tagen (Rückstand des
 * jüngsten Ads-Tages) -- welches, entscheidet die Function pro Befund. Ein
 * fest angehängtes ` h` machte aus einem neuntägigen Ausfall die Anzeige
 * "9.0 h", also einen Faktor 24 zu klein, und zwar direkt über der Zeile
 * "Alter des jüngsten Ads-Tages in Tagen", die daneben die Wahrheit sagt.
 * Die Einheit steht deshalb dort, wo sie hingehört: in `value_label`, das
 * die Function zusammen mit dem Wert liefert und passend zum Kriterium
 * setzt. Lieber eine nackte Zahl mit richtiger Beschriftung als eine Zahl
 * mit falscher Einheit.
 */
const GUARDRAIL_META: Record<string, { title: string; rule: string; reaction: string; unit?: string }> = {
  'G-A': {
    title: 'Armblinde Klicks',
    rule: 'Gepoolte Wochenklicks gegen das Pre-Wochenmittel. Reißt bei unter 70 Prozent in zwei aufeinanderfolgenden Wochen.',
    reaction: 'Bei Riss: Eskalation zur Diagnose, kein Rollback',
    unit: ' %',
  },
  'G-B': {
    title: 'Disapprovals im Merchant Center',
    rule: 'Quote über 5 Prozent des Feeds oder ein Sprung von mehr als 2 Prozentpunkten binnen 48 Stunden.',
    reaction: 'Bei Riss: sofortige Meldung',
    unit: ' %',
  },
  'G-C': {
    title: 'Budget-Pacing',
    rule: 'Tagesausgabe unter 50 Prozent des Strukturbudgets an drei Tagen oder über 130 Prozent.',
    reaction: 'Bei Riss: Review von tROAS und Deckeln',
    unit: ' %',
  },
  'G-D': {
    title: 'Frische der Ingest-Daten',
    rule: 'Die Ads-Daten dürfen nicht älter als 48 Stunden sein, und es müssen '
      + 'überhaupt noch neue Tage ankommen.',
    reaction: 'Bei Riss: Label-Rotation gesperrt, Meldung raus',
    // Keine feste Einheit -- siehe Kopf. Die Einheit steht in `value_label`.
  },
  // Ergebniswache nach Messplan §7 Kriterium 1 (24.09.2026), kein
  // Messplan-Guardrail im Sinne von G-A bis G-D und kein Riegel: G-E meldet
  // nur. Quelle ist die Kostentabelle über ALLE Kampagnen (nicht nur die
  // Struktur-Kampagnen), Kontrolle golfimport und Vorjahr stehen als Kontext daneben,
  // nie als Umdeutung.
  'G-E': {
    title: 'Conversion-Wert',
    rule: 'Konto-Conversion-Wert je Kalenderwoche gegen das eingefrorene Pre-Wochenmittel, über alle Kampagnen. '
      + 'Gewertet erst drei Tage nach Wochenende (Nachattribution). '
      + 'Warnung bei einer Woche unter 60 Prozent, Riss bei zwei aufeinanderfolgenden.',
    reaction: 'Bei Riss: Diagnose nach Messplan §7, ob eine externe Erklärung vorliegt '
      + '(Saison, Ausfall, Feed-Problem); Kontrolle und Vorjahr stehen im Befund. '
      + 'Nie Auto-Rollback, G-E sperrt nichts',
    unit: ' %',
  },
  // Treffsicherheit (26.09.2026, Ads-Ausbau 0.8), kein Messplan-Guardrail und
  // kein Riegel: G-T meldet nur. Grundlage ist der Messplan-Nachtrag E1 §6.
  'G-T': {
    title: 'Treffsicherheit der Heroes',
    rule: 'Wie viel besser die Heroes eines übernommenen Laufs in der Folgewoche liefen als die '
      + 'übrigen Produkte mit Anzeigen, ROAS-Vorsprung in Prozent. Gewertet drei Tage nach dem '
      + 'Ende seiner Folgewoche, nur ab 50 CHF Kosten je Seite und 30 Conversions. Hinsehen, '
      + 'wenn zwei ausgewertete Läufe hintereinander keinen Vorsprung haben.',
    reaction: 'Bei Hinweis: Tiering und Struktur prüfen. Seit dem Cutover misst v1 die Hero-Kampagne '
      + 'mit, der Vergleich mit v2 läuft über den Schatten. G-T sperrt nichts',
    unit: ' %',
  },
  // Struktur-Check, kein Messplan-Guardrail (05.09.2026): steht im selben
  // Protokoll und wird hier mit Überschrift gerendert, sperrt aber keine
  // Rotation. Anlass: die Zombie-Shopping-Kampagnen lieferten vier Tage lang
  // null Impressionen (Anzeige seit dem Bau pausiert), und keiner der vier
  // Guardrails hat es gesehen.
  'G-S': {
    title: 'Struktur-Stille',
    rule: 'Eine registrierte Struktur-Kampagne liefert seit ihrer Aktivierung an gemessenen Tagen '
      + 'keine Impression. Warnung ab 2, Riss ab 4 Tagen.',
    reaction: 'Bei Riss: Kampagne, Anzeigengruppe und Anzeige in der Ads-UI prüfen, inspect-campaign lesen',
    unit: ' Tage',
  },
  // Datencheck, kein Messplan-Guardrail (07.09.2026), sperrt die Rotation
  // aber über die Abnahme-Prüfung attribution_complete. Anlass: 44.600
  // mehrdeutige Pull-Zeilen je Nacht standen fünf Nächte lang nur im Log,
  // während G-D das Konto als frisch meldete.
  'G-Z': {
    title: 'Zuordnungsquote des Ads-Pulls',
    rule: 'Anteil der Antwortzeilen des nächtlichen Ads-Pulls, die einem Produkt zugeordnet wurden. '
      + 'Warnung unter 90 Prozent oder bei einem Einbruch um mehr als 5 Punkte zur Vorwoche, Riss unter 70 Prozent.',
    reaction: 'Bei Riss: Label-Rotation gesperrt; Feeds mit gleicher Sprache und gleichem Feed-Label prüfen, [ads-pull]-Log lesen',
    unit: ' %',
  },
  // Abdeckungs-Checks (24.09.2026), kein Messplan-Guardrail, kein Riegel.
  'G-L': {
    title: 'Label-Deckung',
    rule: 'Anteil der zuletzt rotierten Produkte, die im Sync-Echo ihr fo_perf-Label tragen. Warnung unter 98 Prozent oder ohne Rotation seit 8 Tagen, Riss unter 90 Prozent.',
    reaction: 'Bei Riss: Label-Verlust je Feed lesen (detail.feeds), Sonde ads-label-probe auf Produkte ohne Label, Ledger Label-Verlust. Der Befund kommt aus ads-coverage-check (08:40), nicht aus dem Guardrail-Lauf',
    unit: ' %',
  },
  'G-K': {
    title: 'Kampagnen-Abdeckung',
    rule: 'Anteil der auslieferbaren Produkte mit Tier, die in genau einer aktiven Struktur-Kampagne ELIGIBLE sind. Warnung unter 99,5 Prozent, Riss unter 99 Prozent oder bei mehr als 1 Prozent doppelt abgedeckter Produkte.',
    reaction: 'Bei Riss: Lückenliste im Befund lesen, Baum per inspect-campaign prüfen; ein Umbau ist eine Entscheidungsvorlage, kein Automatismus',
    unit: ' %',
  },
};

/** Die Reihenfolge der Tafel. Ein Guardrail ausserhalb dieser Liste wird
 *  trotzdem gerendert, nur hinten -- die Spalte hat bewusst keinen
 *  CHECK-Constraint, und die Fläche soll ihn nicht nachträglich einführen. */
const GUARDRAIL_ORDER = ['G-A', 'G-B', 'G-C', 'G-D', 'G-E', 'G-T', 'G-Z', 'G-S', 'G-L', 'G-K'];

const SEVERITY_STYLE: Record<AdsGuardrailSeverity, {
  dot: string; text: string; badge: string; word: string; border: string;
}> = {
  // "unknown" ist bewusst neutral und NICHT grün: nicht gemessen darf nicht
  // aussehen wie gemessen und in Ordnung.
  unknown: {
    dot: 'bg-muted-foreground/50', text: 'text-muted-foreground',
    badge: 'bg-muted text-muted-foreground', word: 'nicht messbar',
    border: 'border-border',
  },
  info: {
    dot: 'bg-success', text: 'text-success-ink',
    badge: 'bg-success-light text-success-ink', word: 'ruhig',
    border: 'border-border',
  },
  warn: {
    dot: 'bg-warning', text: 'text-warning-ink',
    badge: 'bg-warning-light text-warning-ink', word: 'hinsehen',
    border: 'border-warning/40',
  },
  breach: {
    dot: 'bg-destructive', text: 'text-destructive-ink',
    badge: 'bg-destructive-light text-destructive-ink', word: 'gerissen',
    border: 'border-destructive/50',
  },
};

function GuardrailCard({ check }: { check: AdsGuardrailCheck }) {
  const meta = GUARDRAIL_META[check.guardrail];
  const style = SEVERITY_STYLE[check.severity];
  return (
    <Card className={`border ${style.border}`} data-testid={`ads-guardrail-${check.guardrail}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${style.badge}`}>
              {check.guardrail}
            </span>
            <CardTitle className="mt-2 text-base">
              {meta?.title ?? check.guardrail}
            </CardTitle>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-semibold tabular-nums ${style.text}`}>
              {check.value === null ? NO_VALUE : `${dec(check.value, 1)}${meta?.unit ?? ''}`}
            </div>
            {check.value_label && (
              <div className="text-xs text-muted-foreground">{check.value_label}</div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-muted-foreground">{meta?.rule ?? 'Regel nicht hinterlegt.'}</p>
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full ${style.dot}`} aria-hidden="true" />
          <span className={`font-medium ${style.text}`}>{style.word}</span>
        </div>
        {check.message && <p className="text-muted-foreground">{check.message}</p>}
        <p className="text-xs text-muted-foreground">{meta?.reaction ?? ''}</p>
      </CardContent>
    </Card>
  );
}

/**
 * Die Wochenreihe von G-A als Balken, mit der 70-Prozent-Linie.
 *
 * Die vier Pre-Wochen stehen als Bezugsgrösse mit dabei und sind heller
 * gezeichnet -- sie werden nie bewertet. Eine Woche ohne vollständige sieben
 * Tage bekommt eine gestrichelte Kontur statt einer Füllung: sie ist gemessen,
 * aber sie zählt nicht in ein Verdikt, und beides gleich zu zeichnen wäre eine
 * Behauptung über eine Zahl, die keine ist.
 */
function GuardrailWeekChart({ weeks }: { weeks: AdsGuardrailWeek[] }) {
  if (weeks.length === 0) return null;
  const measured = weeks.filter((w) => w.pct_of_pre !== null);
  if (measured.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Noch keine Bezugsgröße: das Pre-Fenster ist erst vollständig, wenn alle 28 Tage
        Ads-Daten tragen. Bis dahin bleibt der Wert unbekannt statt geschätzt.
      </p>
    );
  }
  const label = measured
    .map((w) => `KW ${w.iso_week ?? '?'}: ${dec(w.pct_of_pre, 0)} Prozent`)
    .join(', ');

  // Drei Zeilen statt einer: Zahlen oben, die Zeichenflaeche mit fester Hoehe
  // in der Mitte, die Wochen darunter. Vorher stand die Zahl IN der Spalte des
  // Balkens, und die Spalte hatte keine feste Hoehe -- `height: 95%` loeste
  // gegen eine inhaltshohe Spalte auf, jeder Balken war 0 px hoch (E2E
  // 24.09.2026, Produktion). Ausserdem bezieht sich die Schwellenlinie jetzt
  // auf dieselbe Flaeche wie die Balken und nicht auf eine, in der noch die
  // Zahlenzeile steckt.
  return (
    <div className="space-y-2">
      <div className="flex gap-3" aria-hidden="true">
        {weeks.map((week) => (
          <span key={week.week_start} className="flex-1 text-center text-xs tabular-nums text-muted-foreground">
            {week.pct_of_pre === null ? NO_VALUE : `${dec(week.pct_of_pre, 0)} %`}
          </span>
        ))}
      </div>
      <div
        className="relative flex h-36 items-end gap-3 border-b border-border"
        role="img"
        aria-label={`Wochenklicks gegen das Pre-Mittel. ${label}. Schwelle 70 Prozent.`}
        data-testid="ga-chart-plot"
      >
        {/* 70-Prozent-Linie. 110 Prozent ist die Skalenhöhe, damit eine Woche
            leicht über dem Mittel nicht am oberen Rand klebt. */}
        <div
          className="pointer-events-none absolute inset-x-0 border-t border-dashed border-destructive/50"
          style={{ bottom: `${(70 / 110) * 100}%` }}
          aria-hidden="true"
        />
        {weeks.map((week) => {
          const pct = week.pct_of_pre;
          const height = pct === null ? 12 : Math.min(Math.max((pct / 110) * 100, 2), 100);
          // days_measured, NICHT days_with_data: eine vollstaendig dunkle Woche
          // hat null Zeilen und ist trotzdem gemessen. Sie gehoert als roter
          // Balken auf null gezeichnet, nicht als gestricheltes Datenloch -
          // sonst behauptet die Flaeche das Gegenteil des Befunds daneben.
          const incomplete = !week.is_pre && (week.days_measured ?? 7) < 7;
          const tone = week.is_pre
            ? 'bg-muted-foreground/30'
            : pct !== null && pct < 70
              ? 'bg-destructive'
              : 'bg-primary';
          return (
            <div key={week.week_start} className="flex h-full flex-1 items-end">
              <div
                data-testid="chart-bar"
                className={incomplete
                  ? 'w-full rounded-t border border-dashed border-muted-foreground/60'
                  : `w-full rounded-t ${tone}`}
                style={{ height: `${height}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-3">
        {weeks.map((week) => (
          <span key={week.week_start} className="flex-1 text-center text-xs text-muted-foreground">
            KW {week.iso_week ?? '?'}
            {week.is_pre && <span className="block text-[10px]">vorher</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Die Wochenreihe von G-E als Balken, mit der Schwelle aus dem Befund.
 *
 * Anders als G-A trägt `detail.weeks` von G-E bereits das fertige Urteil je
 * Woche (`scored`, `settled`, `below_threshold`) -- der Watchdog hat
 * gerechnet, diese Komponente liest nur nach und zeichnet nichts Eigenes
 * hinein. Eine Woche ohne `scored` bekommt dieselbe gestrichelte Kontur wie
 * bei G-A statt einer Füllung: sie zählt nicht in ein Verdikt, und beides
 * gleich zu zeichnen wäre eine Behauptung über eine Zahl, die keine ist.
 * Kontrolle (golfimport) und Vorjahr stehen als Zahl unter jedem Balken einer
 * Messwoche -- Kontext für die Diagnose, nie ein Ersatzmaßstab.
 */
function GuardrailGeChart({
  weeks,
  thresholdPct,
  isSelfControl,
}: {
  weeks: AdsGuardrailGeWeek[];
  thresholdPct: number;
  /**
   * M4 (Final-Review Ads-Ausbau 0.4, 24.09.2026): dieses Konto IST die
   * Negativkontrolle. Dann gibt es keine zweite Zeile "Kontrolle", gegen die
   * verglichen werden koennte -- "Kontrolle: nicht messbar" waere hier eine
   * Falschaussage (es fehlt nicht die Messung, die Frage stellt sich nicht).
   */
  isSelfControl: boolean;
}) {
  const measured = weeks.filter((w) => w.pct_of_pre !== null);
  if (weeks.length === 0 || measured.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Noch keine Bezugsgröße oder noch keine Messwoche: G-E bleibt bis dahin nicht
        gemessen, nicht grün.
      </p>
    );
  }
  // M3 (Final-Review Ads-Ausbau 0.4, 24.09.2026): das aria-label nennt fuer
  // JEDE Woche einen Satz -- Pre-Wochen als "vorher" markiert, gewertete
  // Messwochen mit ihrer Prozentzahl, ungewertete Messwochen ausdruecklich als
  // "nicht gewertet" statt einer Zahl, die (anders als bei einer Pre-Woche)
  // noch nicht das Urteil des Befunds traegt.
  const label = weeks
    .map((w) => {
      if (w.is_pre) {
        return w.pct_of_pre === null
          ? `KW ${w.iso_week ?? '?'} (vorher): nicht messbar`
          : `KW ${w.iso_week ?? '?'} (vorher): ${dec(w.pct_of_pre, 0)} Prozent`;
      }
      if (!w.scored) return `KW ${w.iso_week ?? '?'}: nicht gewertet`;
      return `KW ${w.iso_week ?? '?'}: ${dec(w.pct_of_pre, 0)} Prozent`;
    })
    .join(', ');
  // Skalenhöhe wie bei G-A (110 Prozent), aber nie kleiner als die Schwelle
  // plus Luft -- eine Schwelle von 60 passt in 110, eine höhere darf die
  // Skala mitnehmen.
  const scaleMax = Math.max(110, thresholdPct + 50);

  // Drei Zeilen wie bei G-A (Fehlerbehebung E2E 24.09.2026: Balken 0 px hoch,
  // weil die Spalte keine feste Hoehe hatte): Zahlen oben, Zeichenflaeche mit
  // fester Hoehe, darunter Wochen und Kontext.
  return (
    <div className="space-y-2">
      <div className="flex gap-3" aria-hidden="true">
        {weeks.map((week) => {
          // M3: keine Prozentzahl ueber dem Balken fuer eine ungewertete
          // Messwoche -- `pct_of_pre` kann dort laengst berechnet sein (die
          // Baseline ist eingefroren), traegt aber noch kein Urteil.
          const displayPct = !week.is_pre && !week.scored ? null : week.pct_of_pre;
          return (
            <span key={week.week_start} className="flex-1 text-center text-xs tabular-nums text-muted-foreground">
              {displayPct === null ? NO_VALUE : `${dec(displayPct, 0)} %`}
            </span>
          );
        })}
      </div>
      <div
        className="relative flex h-36 items-end gap-3 border-b border-border"
        role="img"
        aria-label={
          `Wochen-Conversion-Wert gegen das Pre-Mittel. ${label}. `
          + `Schwelle ${dec(thresholdPct, 0)} Prozent.`
        }
        data-testid="ge-chart-plot"
      >
        <div
          className="pointer-events-none absolute inset-x-0 border-t border-dashed border-destructive/50"
          style={{ bottom: `${(thresholdPct / scaleMax) * 100}%` }}
          aria-hidden="true"
        />
        {weeks.map((week) => {
          const pct = week.pct_of_pre;
          const height = pct === null ? 12 : Math.min(Math.max((pct / scaleMax) * 100, 2), 100);
          const unscored = !week.is_pre && !week.scored;
          const tone = week.is_pre
            ? 'bg-muted-foreground/30'
            : week.scored && week.below_threshold
              ? 'bg-destructive'
              : 'bg-primary';
          return (
            <div key={week.week_start} className="flex h-full flex-1 items-end">
              <div
                data-testid="chart-bar"
                className={unscored
                  ? 'w-full rounded-t border border-dashed border-muted-foreground/60'
                  : `w-full rounded-t ${tone}`}
                style={{ height: `${height}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-3">
        {weeks.map((week) => {
          const unscored = !week.is_pre && !week.scored;
          return (
            <div key={week.week_start} className="flex-1 space-y-0.5 text-center text-muted-foreground">
              <span className="block text-xs">KW {week.iso_week ?? '?'}</span>
              {week.is_pre && <span className="block text-[10px]">vorher</span>}
              {!week.is_pre && (
                <div className="space-y-0.5 text-[10px] leading-tight">
                  {unscored && (
                    <span className="block">
                      {/* M3: "reift" nur bei voller Abdeckung, die nur noch
                          nicht ausgereift ist -- sonst "unvollständig". */}
                      {(week.days_measured ?? 0) >= 7 && !week.settled ? 'reift' : 'unvollständig'}
                    </span>
                  )}
                  <span className="block">
                    {isSelfControl
                      ? 'Kontrolle: entfällt (selbst Kontrolle)'
                      : week.control_pct === null
                        ? 'Kontrolle: nicht messbar'
                        : `Kontrolle ${dec(week.control_pct, 0)} %`}
                  </span>
                  <span className="block">
                    {week.prior_year_pct === null
                      ? 'Vorjahr: nicht messbar'
                      : `Vorjahr ${dec(week.prior_year_pct, 0)} %`}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * G-T im Verlauf: je Lauf ein Punkt für v1 (die rotierten Labels) und v2
 * (Schatten) mit 90-Prozent-Intervall, Nulllinie. Unbestimmte Läufe stehen als
 * leerer Kreis ohne Zahl da: die Mindestmenge ist verfehlt, das ist keine Null.
 * Keine Summen (Blindheit).
 *
 * Die Zeichenfläche hat eine feste Höhe (h-40), und jede Tagesspalte füllt sie
 * (h-full). Lehre aus App-PR #397: eine Prozentangabe gegen eine Spalte ohne
 * Höhe ergab 0 px. Die Nulllinie liegt in der Mitte, ein negativer Vorsprung
 * steht darunter und nie auf ihr.
 *
 * Die Skala richtet sich nach den Vorsprüngen, nicht nach den Intervallen
 * (Fix-Runde 1). Der Vorsprung ist ein ROAS-Verhältnis: nach unten bei -100
 * begrenzt, nach oben offen. Ein einziges Schatten-Intervall bis 600 drückte
 * sonst jeden v1-Punkt auf wenige Pixel um die Nulllinie, und das Vorzeichen,
 * also genau das Warnkriterium von G-T, wäre unsichtbar. Ein Intervall, das
 * über die Skala hinausreicht, endet am Rand mit einer Pfeilspitze; seine
 * wahren Grenzen stehen im aria-label und im Befund.
 *
 * golfimport (Negativkontrolle) hat keine rotierten Labels und damit nie eine
 * v1-Reihe. Dort steht der Grund statt einer v1-Zeile "kein Lauf" unter jedem
 * Tag, die fehlende Daten behaupten würde. Trägt die Negativkontrolle doch je
 * einen v1-Punkt, wird er gezeigt: die Fläche versteckt nichts, was der Befund
 * enthält.
 */
function GuardrailGtChart({
  v1,
  v2,
  isNegativeControl,
}: {
  v1: AdsGuardrailGtPoint[];
  v2: AdsGuardrailGtPoint[];
  isNegativeControl: boolean;
}) {
  const showV1 = !isNegativeControl || v1.length > 0;
  const hint = showV1 ? null : (
    <p className="text-xs text-muted-foreground" data-testid="gt-negative-control-hint">
      Keine rotierten Labels (Negativkontrolle): v1 entfällt, gezeigt ist nur der v2-Schatten.
    </p>
  );
  const days = [...new Set([...v1, ...v2].map((p) => p.d0))].sort();
  if (days.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Noch kein ausgereifter Lauf gespeichert. Ein Lauf wird drei Tage nach dem Ende seiner
          Folgewoche bewertet.
        </p>
        {hint}
      </div>
    );
  }
  const find = (list: AdsGuardrailGtPoint[], d0: string) => list.find((p) => p.d0 === d0) ?? null;
  const series = [
    {
      name: 'v1', list: v1, fill: 'bg-primary', ring: 'border-primary',
      capUp: 'border-b-primary', capDown: 'border-t-primary',
    },
    {
      name: 'v2', list: v2, fill: 'bg-muted-foreground', ring: 'border-muted-foreground',
      capUp: 'border-b-muted-foreground', capDown: 'border-t-muted-foreground',
    },
  ]
    .filter((s) => s.name !== 'v1' || showV1)
    .map((s, index, all) => ({ ...s, offset: all.length === 1 ? 0 : index === 0 ? -6 : 6 }));
  // Skala nur aus den Vorsprüngen der gezeigten Reihen, mit 15 Prozent Luft.
  const leads = series.flatMap((s) => s.list)
    .map((p) => p.lead_pct)
    .filter((x): x is number => x !== null);
  const max = Math.max(50, ...leads.map((x) => Math.abs(x))) * 1.15;
  const pos = (x: number) => 50 - (x / max) * 45; // Prozent von oben, 0 in der Mitte
  const clamp = (x: number) => Math.min(Math.max(x, -max), max);
  // Ganze Prozent. Ein Wert zwischen -0,5 und 0 rundet auf null und hiesse
  // sonst "-0": eine Zahl mit Vorzeichen, die es nicht gibt.
  const pct = (x: number) => {
    const text = dec(x, 0);
    return /^[-\u2212]0$/.test(text) ? '0' : text;
  };
  const dayLabel = (d0: string) => `${d0.slice(8, 10)}.${d0.slice(5, 7)}.`;
  // Das Intervall ist in der Fläche nur als Strich zu sehen; es gehört deshalb
  // auch in die Beschriftung, und zwar mit seinen wahren Grenzen.
  const spoken = (name: string, p: AdsGuardrailGtPoint | null) => {
    if (p === null) return `${name} kein Lauf`;
    if (p.lead_pct === null) return `${name} unbestimmt`;
    const interval = p.lead_ci_low !== null && p.lead_ci_high !== null
      ? ` (Intervall ${pct(p.lead_ci_low)} bis ${pct(p.lead_ci_high)} Prozent)`
      : '';
    return `${name} ${pct(p.lead_pct)} Prozent${interval}`;
  };
  const shown = (p: AdsGuardrailGtPoint | null) =>
    p === null ? 'kein Lauf' : p.lead_pct === null ? 'unbestimmt' : `${pct(p.lead_pct)} %`;
  const label = days
    .map((d0) => `Lauf ${dayLabel(d0)}: ${series.map((s) => spoken(s.name, find(s.list, d0))).join(', ')}`)
    .join('; ');
  return (
    <div className="space-y-2">
      <div
        className="relative h-40 border-b border-border"
        role="img"
        aria-label={`Hero-Vorsprung in der Folgewoche je Lauf. ${label}.`}
        data-testid="gt-chart-plot"
      >
        <div
          data-testid="gt-zero-line"
          className="pointer-events-none absolute inset-x-0 border-t border-dashed border-muted-foreground/60"
          style={{ top: `${pos(0)}%` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 flex">
          {days.map((d0) => (
            <div key={d0} data-testid="gt-day-column" className="relative h-full flex-1">
              {series.map((s) => {
                const p = find(s.list, d0);
                if (!p) return null;
                if (p.lead_pct === null) {
                  return (
                    <span
                      key={s.name}
                      data-testid={`gt-point-${s.name}-open`}
                      className={`absolute h-2.5 w-2.5 rounded-full border ${s.ring} bg-background`}
                      style={{ top: `calc(${pos(0)}% - 5px)`, left: `calc(50% + ${s.offset}px - 5px)` }}
                    />
                  );
                }
                const low = p.lead_ci_low;
                const high = p.lead_ci_high;
                const hasCi = low !== null && high !== null;
                return (
                  <span key={s.name}>
                    {hasCi && (
                      <span
                        data-testid={`gt-ci-${s.name}`}
                        className={`absolute w-px ${s.fill}`}
                        style={{
                          top: `${pos(clamp(high))}%`,
                          height: `${Math.max(pos(clamp(low)) - pos(clamp(high)), 0)}%`,
                          left: `calc(50% + ${s.offset}px)`,
                        }}
                      />
                    )}
                    {hasCi && high > max && (
                      <span
                        data-testid={`gt-ci-${s.name}-clipped`}
                        data-end="high"
                        className={`absolute h-0 w-0 border-x-4 border-b-[6px] border-x-transparent ${s.capUp}`}
                        style={{ top: `calc(${pos(max)}% - 6px)`, left: `calc(50% + ${s.offset}px - 4px)` }}
                      />
                    )}
                    {hasCi && low < -max && (
                      <span
                        data-testid={`gt-ci-${s.name}-clipped`}
                        data-end="low"
                        className={`absolute h-0 w-0 border-x-4 border-t-[6px] border-x-transparent ${s.capDown}`}
                        style={{ top: `${pos(-max)}%`, left: `calc(50% + ${s.offset}px - 4px)` }}
                      />
                    )}
                    <span
                      data-testid={`gt-point-${s.name}`}
                      className={`absolute h-2.5 w-2.5 rounded-full ${s.fill}`}
                      style={{ top: `calc(${pos(p.lead_pct)}% - 5px)`, left: `calc(50% + ${s.offset}px - 5px)` }}
                    />
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="flex">
        {days.map((d0) => (
          <div
            key={d0}
            className="min-w-0 flex-1 space-y-0.5 text-center text-[10px] leading-tight text-muted-foreground"
          >
            <span className="block text-xs">{dayLabel(d0)}</span>
            {showV1 && <span className="block tabular-nums">{`v1 ${shown(find(v1, d0))}`}</span>}
            <span className="block tabular-nums">{`v2 ${shown(find(v2, d0))}`}</span>
          </div>
        ))}
      </div>
      {hint}
    </div>
  );
}

function GuardrailBoard({
  report,
  isLoading,
  error,
  onRetry,
  accountLabel,
  isNegativeControl,
}: {
  report: AdsGuardrailReport | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  accountLabel: string;
  /** golfimport: keine rotierten Labels, G-T hat dort nie eine v1-Reihe. */
  isNegativeControl: boolean;
}) {
  const checks = report?.checks ?? [];
  const ordered = [...checks].sort((a, b) => {
    const ia = GUARDRAIL_ORDER.indexOf(a.guardrail);
    const ib = GUARDRAIL_ORDER.indexOf(b.guardrail);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  const ga = ordered.find((c) => c.guardrail === 'G-A') ?? null;
  const ge = ordered.find((c) => c.guardrail === 'G-E') ?? null;
  const gt = ordered.find((c) => c.guardrail === 'G-T') ?? null;
  const geThresholdPct = guardrailGeThresholdPct(ge);
  const geIsSelfControl = guardrailGeIsSelfControl(ge);
  const checkedAt = ordered.reduce<string | null>(
    (latest, c) => (c.checked_at && (!latest || c.checked_at > latest) ? c.checked_at : latest),
    null,
  );

  return (
    <section className="space-y-3" aria-labelledby="ads-guardrails-heading">
      <div className="flex items-baseline justify-between gap-4">
        <h2 id="ads-guardrails-heading" className="text-lg font-semibold">Guardrails</h2>
        <span className="text-sm text-muted-foreground">
          {checkedAt ? `Zuletzt geprüft ${dateTime(checkedAt)}` : 'Noch nicht geprüft'}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        Befunde für {accountLabel}, jeden Tag geschrieben, auch wenn nichts auffällt.
      </p>

      {isLoading && <Skeleton className="h-48 w-full rounded-lg" />}

      {!isLoading && error != null && (
        <ErrorState
          title="Guardrail-Befunde nicht geladen"
          description={errorText(error)}
          onRetry={onRetry}
        />
      )}

      {!isLoading && error == null && ordered.length === 0 && (
        <EmptyState
          icon={ShieldAlert}
          title="Noch kein Guardrail-Lauf"
          description={
            'Der tägliche Lauf schreibt ab dem 01.09. je Guardrail und Konto eine Zeile. '
            + 'Dass hier nichts steht, heißt "noch nicht gemessen" und nicht "alles in Ordnung".'
          }
        />
      )}

      {!isLoading && error == null && ordered.length > 0 && (
        <>
          <div className="grid gap-3 md:grid-cols-2">
            {ordered.map((check) => (
              <GuardrailCard key={`${check.ads_customer_id}-${check.guardrail}`} check={check} />
            ))}
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">G-A im Verlauf</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <GuardrailWeekChart weeks={guardrailWeeks(ga)} />
              <p className="text-xs text-muted-foreground">
                Bewusst armblind gerechnet und auf Klicks beschränkt. G-A ist die
                Volumen-Sicherung, nicht das Ergebnis; den Conversion-Wert beobachtet G-E,
                ROAS wertet erst die Auswertung nach dem Messplan-Nachtrag.
              </p>
            </CardContent>
          </Card>

          {ge && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">G-E im Verlauf</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <GuardrailGeChart
                  weeks={guardrailGeWeeks(ge)}
                  thresholdPct={geThresholdPct}
                  isSelfControl={geIsSelfControl}
                />
                <p className="text-xs text-muted-foreground">
                  Konto-Conversion-Wert gegen das Pre-Wochenmittel, alle Kampagnen. Kontrolle
                  golfimport und Vorjahr sind Kontext für die Diagnose, keine Umdeutung; G-E
                  meldet nur.
                </p>
                {geIsSelfControl && (
                  <p className="text-xs text-muted-foreground">
                    Dieses Konto ist selbst die Kontrolle.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {gt && (
            <Card data-testid="ads-guardrail-gt-history">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">G-T im Verlauf</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <GuardrailGtChart
                  v1={guardrailGtSeries(gt, 'v1')}
                  v2={guardrailGtSeries(gt, 'v2')}
                  isNegativeControl={isNegativeControl}
                />
                <p className="text-xs text-muted-foreground">
                  Hero-Vorsprung in der Folgewoche mit 90-Prozent-Intervall, v1 als gefüllter Punkt,
                  v2-Schatten grau. Seit dem Cutover laufen die v1-Heroes in einer eigenen Kampagne;
                  der v1-Wert misst diese Struktur mit und ist mit v2 nur über den Schatten
                  vergleichbar. Leerer Kreis: unbestimmt, Mindestmenge verfehlt oder die übrigen
                  Produkte ohne Verkauf, keine Null. Ein Pfeil am Rand heißt: das Intervall reicht
                  über die Skala hinaus. G-T meldet nur.
                </p>
              </CardContent>
            </Card>
          )}

          <Card className="bg-muted/40">
            <CardContent className="space-y-1 pt-4 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Was hier nie passiert</p>
              <p>Kein automatischer Rollback. Ein gerissener Guardrail meldet, er handelt nicht.</p>
              <p>Keine Kampagne wird von hier aus aktiviert oder pausiert.</p>
              <p>G-D sperrt die Label-Rotation wirklich, statt sie nur anzuzeigen.</p>
            </CardContent>
          </Card>
        </>
      )}
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Abschnitt 2 -- Tiering-Lauf (Artboard 2)
// ═════════════════════════════════════════════════════════════════════════════

function TransitionRow({ transition, max }: { transition: AdsTransition; max: number }) {
  const width = max > 0 ? Math.max(2, Math.round((transition.n / max) * 100)) : 0;
  return (
    <TableRow>
      <TableCell>
        <span className="flex items-center gap-2">
          {transition.from ? (
            <TierPill tier={transition.from} />
          ) : (
            <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
              ohne Tier
            </span>
          )}
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <TierPill tier={transition.to} />
        </span>
      </TableCell>
      <TableCell className="text-right text-sm font-semibold tabular-nums">
        {int(transition.n)}
      </TableCell>
      <TableCell className="w-[35%]">
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full rounded-full ${
              transition.damping === 'pending' ? 'bg-muted-foreground/50' : 'bg-success'
            }`}
            style={{ width: `${width}%` }}
          />
        </div>
      </TableCell>
      <TableCell className={`text-xs font-medium ${DAMPING_TONE[transition.damping]}`}>
        {DAMPING_TEXT[transition.damping]}
      </TableCell>
    </TableRow>
  );
}

interface TieringSectionProps {
  account: AdsOverviewAccount;
  pilot: AdsPilotInfo | null;
  dryRun: AdsDryRunResult | null;
  windowDays: number;
  onWindowDaysChange: (days: number) => void;
  onDryRun: () => void;
  onApply: () => void;
  isRunning: boolean;
  isApplying: boolean;
  applyBlocked: TierApplyBlock | null;
}

function TieringSection({
  account,
  pilot,
  dryRun,
  windowDays,
  onWindowDaysChange,
  onDryRun,
  onApply,
  isRunning,
  isApplying,
  applyBlocked,
}: TieringSectionProps) {
  const pilotLabel = pilot?.label ?? null;
  // Der TROCKENLAUF bleibt der Negativkontrolle ausdruecklich erlaubt.
  //
  // Er war hier kurzzeitig gesperrt, und das war eine Ueberdehnung: er
  // schreibt nichts an die Produkte und nichts zu Google, nur unsere eigene
  // Buchfuehrung (Laufkopf plus Item-Zeilen). Die harte Grenze zaehlt auf
  // "kein Tier, kein Label, keine Kampagnenberuehrung" -- ein Trockenlauf
  // vergibt kein Tier an ein Produkt (`products.performance_tier` bleibt
  // unberuehrt), und die Oberflaeche sagt es selbst: "rechnet und haelt sein
  // Ergebnis fest, schreibt aber nichts an die Produkte".
  //
  // Ausserdem ist es gelebte Praxis: sechs Trockenlaeufe auf golfimport am
  // 22. und 23.08., keiner davon je uebernommen. Er beantwortet die Frage,
  // was das Tiering ueber die Kontrolle SAGEN wuerde -- fuer die Messung
  // interessant, und genau deshalb ohne Folgen.
  //
  // ⚠ Was hier NICHT steht und Dennis entscheiden muss: `dry_run` ist die
  // einzige schreibende Aktion, die serverseitig GAR KEINE Kontopruefung
  // kennt -- weder `decideWriteEligibility` noch `write_enabled` in SQL.
  // Soll die Kontrolle wirklich nie einen Trockenlauf bekommen, gehoert der
  // Riegel in `ads_tier_run_begin`, nicht in ein `disabled` an einem Knopf.
  const isControl = account.role === 'negative_control';
  const run = dryRun?.run ?? null;
  const transitions = dryRun?.transitions ?? [];
  const reasons = dryRun?.reasons ?? [];
  const maxTransition = transitions.reduce((max, entry) => Math.max(max, entry.n), 0);
  const maxReason = reasons.reduce((max, entry) => Math.max(max, entry.n), 0);
  const willApply = run ? run.n_immediate + run.n_confirmed : 0;
  const age = runAgeHours(run);

  return (
    <section className="space-y-3" aria-labelledby="ads-tiering-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="ads-tiering-heading" className="text-lg font-semibold">
          Tiering-Lauf, Trockenlauf
        </h2>
        <div className="flex items-center gap-2">
          <Select
            value={String(windowDays)}
            onValueChange={(value) => onWindowDaysChange(Number(value))}
          >
            <SelectTrigger className="w-[150px]" aria-label="Fensterlänge">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[14, 28, 56].map((days) => (
                <SelectItem key={days} value={String(days)}>
                  Fenster {days} Tage
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={onDryRun} disabled={isRunning || !account.connected}>
            {isRunning ? 'rechnet …' : 'Trockenlauf rechnen'}
          </Button>
        </div>
      </div>

      {/* Auch ohne Sperre gehoert der Hinweis hierher: wer den Abschnitt mit
          ausgewaehlter Kontrolle sieht, soll wissen, dass ein Trockenlauf hier
          rechnet und NICHT schreibt -- und dass "Uebernehmen" darunter
          gesperrt bleibt. */}
      {isControl && <BlockNote>{blockText('negative_control', pilotLabel)}</BlockNote>}

      {!run ? (
        <div className="space-y-3">
          <EmptyState
            icon={Layers}
            title={`Noch kein Trockenlauf für ${account.label}`}
            description={
              account.connected
                ? 'Der Trockenlauf rechnet und hält sein Ergebnis fest, schreibt aber nichts an die Produkte. Ohne einen angezeigten Lauf bleibt "Übernehmen" gesperrt.'
                : 'Dieses Konto hat keine OAuth-Zeile. Es kann weder gerechnet noch geschrieben werden.'
            }
          />
          {/* LESEKONTEXT, KEIN SCHREIBGRUND.
              Der angezeigte Lauf lebt in der Sitzung und ist nach einem
              Neuladen oder einem Kontowechsel weg. So soll es sein, das traegt
              harte Grenze 3. Es kostete bis hierher aber auch die ZAHLEN: wer
              wissen wollte, was der letzte Lauf ergeben hat, musste neu
              rechnen. Der gespeicherte Laufkopf steht ohnehin in der
              Uebersicht; ihn zu zeigen nimmt der Regel nichts, denn
              "Uebernehmen" haengt unveraendert an `shownRun` und nicht an
              dieser Karte.
              Bewusst OHNE Wanderung und Begruendungs-Codes: die kommen nur aus
              der Antwort des Trockenlaufs, und eine halb gefuellte Ansicht
              waere schlechter als eine ehrlich leere.

              UND BEWUSST OHNE DIE TIER-PILLEN. Hier standen bis zum 23.08. die
              vier `account.tiers`-Pillen neben "N mit neuem Tier" -- in EINER
              Zeile, unter einer Überschrift, die den Lauf datiert. Die Zahlen
              kommen aber aus zwei Zeitpunkten: `n_changes` gehört dem Lauf,
              `account.tiers` ist ein LIVE-Zählstand über
              `products.performance_tier`. Ist der letzte Lauf ein nie
              übernommener Trockenlauf, zeigen die Pillen den Stand eines
              FRÜHEREN Laufs, und die Zeile las sich trotzdem wie eine einzige
              Aussage.
              Weglassen statt umbeschriften, aus zwei Gründen: die Pillen
              stehen keine Bildschirmhöhe weiter oben auf der Kontokarte
              ohnehin schon, dort ohne Datum und damit richtig gerahmt; und ein
              Zusatz wie "heutiger Bestand" hätte die Doppelung behalten und
              dem Leser zwei Zeitbezüge in einer Zeile zugemutet. An ihrer
              Stelle steht jetzt die Dämpfungs-Aufteilung DIESES Laufs -- eine
              Zahl, die die Kontokarte nicht hat, und alles in der Karte kommt
              damit aus einer Quelle zu einem Zeitpunkt. */}
          {account.latest_run && (
            <Card className="border-dashed bg-muted/30">
              <CardContent className="space-y-2 p-4">
                <p className="text-xs font-medium text-muted-foreground">
                  Zuletzt gespeicherter Lauf vom {dateTime(account.latest_run.created_at)}
                  {' · '}
                  <span className={RUN_STATUS[account.latest_run.status].tone}>
                    {RUN_STATUS[account.latest_run.status].text}
                  </span>
                  {' · '}Fenster {dateOnly(account.latest_run.window_start)} bis{' '}
                  {dateOnly(account.latest_run.window_end)}. Nur zum Lesen.
                </p>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5 text-sm tabular-nums">
                  <span>{int(account.latest_run.n_changes)} mit neuem Tier</span>
                  <span className="text-xs text-muted-foreground">
                    {int(account.latest_run.n_immediate + account.latest_run.n_confirmed)} sofort
                    oder bestätigt, {int(account.latest_run.n_pending)} gedämpft
                  </span>
                </div>
                {account.latest_run.status === 'applying' && <InterruptedApplyNote run={account.latest_run} />}
                <p className="text-xs text-muted-foreground">
                  Für die Übernahme zählt dieser Lauf nicht. Dafür braucht es einen Trockenlauf,
                  der in dieser Sitzung gerechnet und angezeigt wurde.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardContent className="grid gap-4 p-5 sm:grid-cols-3">
              <div>
                <div className="text-2xl font-bold tabular-nums">{int(run.n_changes)}</div>
                <p className="text-xs text-muted-foreground">Produkte mit neuem Tier</p>
              </div>
              <div>
                <div className="text-2xl font-bold text-success-ink tabular-nums">
                  {int(willApply)}
                </div>
                <p className="text-xs text-muted-foreground">
                  werden übernommen ({int(run.n_immediate)} sofort, {int(run.n_confirmed)}{' '}
                  bestätigt)
                </p>
              </div>
              <div>
                <div className="text-2xl font-bold tabular-nums">{int(run.n_pending)}</div>
                <p className="text-xs text-muted-foreground">gedämpft, warten auf Bestätigung</p>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground tabular-nums">
            Lauf {run.id} {' · '} gerechnet {dateTime(run.created_at)}
            {age !== null && ` (vor ${dec(age, 1)} h)`}
            {' · '} Fenster {dateOnly(run.window_start)} bis {dateOnly(run.window_end)}
            {run.window_days !== null && `, ${int(run.window_days)} Tage`}
            {' · '} Kontoziel ROAS {dec(run.roas_target, 2)}
            {run.logic_version && ` · Logik ${run.logic_version}`}
            {run.data_through && ` · Daten bis ${dateOnly(run.data_through)}`}
          </p>

          {dryRun && dryRun.warnings.length > 0 && (
            <WarnBanner title="Warnungen dieses Laufs">
              <ul className="list-disc space-y-0.5 pl-5">
                {dryRun.warnings.map((warning) => (
                  <li key={warning}>
                    {warning === 'ingest_stale'
                      ? blockText('ingest_stale', pilotLabel)
                      : `Warnung: ${warning}`}
                  </li>
                ))}
              </ul>
            </WarnBanner>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Wanderung zwischen den Segmenten</CardTitle>
              <p className="text-xs text-muted-foreground">
                Ein Wechsel wird erst übernommen, wenn zwei aufeinanderfolgende Läufe ihn
                bestätigen. Der Weg aus zombie heraus ist davon ausgenommen: die ersten Ads-Daten
                sind ein echter Zustandswechsel und kein Rauschen. Stillstand steht nicht in dieser
                Tafel.
              </p>
            </CardHeader>
            <CardContent>
              {transitions.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Keine Wanderung. Jedes Produkt bleibt in seinem Segment.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[190px]">Wechsel</TableHead>
                        <TableHead className="text-right">Produkte</TableHead>
                        <TableHead>Anteil</TableHead>
                        <TableHead>Dämpfung</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transitions.map((transition) => (
                        <TransitionRow
                          key={`${transition.from ?? 'none'}-${transition.to}-${transition.damping}`}
                          transition={transition}
                          max={maxTransition}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Begründungs-Codes dieses Laufs</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Reine Diagnose. Die Codes stehen im Bericht, wandern aber nie in den Feed. Ein
                  Produkt kann mehrere tragen.
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {reasons.length === 0 ? (
                  <p className="py-4 text-sm text-muted-foreground">
                    Kein Produkt trägt einen Begründungs-Code.
                  </p>
                ) : (
                  reasons.map((reason) => (
                    <div key={reason.code} className="flex items-center gap-3">
                      <span className="w-44 shrink-0 truncate font-mono text-xs" title={reason.code}>
                        {reason.code}
                      </span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{
                            width: `${maxReason > 0 ? Math.max(2, Math.round((reason.n / maxReason) * 100)) : 0}%`,
                          }}
                        />
                      </div>
                      <span className="w-16 shrink-0 text-right text-sm tabular-nums">
                        {int(reason.n)}
                      </span>
                      <span className="hidden w-28 shrink-0 text-xs text-muted-foreground sm:block">
                        {REASON_NOTE[reason.code] ?? ''}
                      </span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Übernehmen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Schreibt das Tier in die Datenbank. Der Feed bei Google bleibt unberührt, dafür
                  ist Schritt 3 zuständig.
                </p>

                {applyBlocked ? (
                  <>
                    <Button className="w-full" disabled>
                      {int(willApply)} Wechsel übernehmen
                    </Button>
                    <BlockNote>{blockText(applyBlocked, pilotLabel)}</BlockNote>
                  </>
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button className="w-full" disabled={isApplying}>
                        {isApplying
                          ? 'übernimmt …'
                          : `${int(willApply)} Wechsel übernehmen`}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Tier-Wechsel übernehmen?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <span className="block space-y-2 text-sm text-muted-foreground">
                            <span className="block">
                              {account.label}: {int(willApply)} von {int(run.n_changes)} Wechseln
                              werden in <code>products.performance_tier</code> geschrieben.{' '}
                              {int(run.n_pending)} bleiben gedämpft und warten auf den zweiten
                              Lauf.
                            </span>
                            <span className="block font-medium text-foreground">
                              Der Feed bei Google ändert sich dadurch nicht. Übernommen wird
                              genau das, was dieser Trockenlauf zeigt.
                            </span>
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={onApply}>Übernehmen</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}

                {/* Der Trockenlauf ist bereits festgehalten (Entscheidung E6:
                    die RPC persistiert ihr Ergebnis). Ein zweiter Knopf "nur
                    Bericht speichern" wie im Entwurf hätte nichts zu tun. */}
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  Der Lauf ist bereits als Bericht festgehalten, auch ohne Übernahme. Die
                  Tier-Logik bleibt bis zum Ende des Messfensters unverändert: eine Änderung an
                  Schwellen oder Definitionen während des Fensters gilt als dokumentierter Abbruch
                  der Messung.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Abschnitt 3 -- Label-Upload (Artboard 3)
// ═════════════════════════════════════════════════════════════════════════════

function slotText(slot: number | null): string {
  return slot === null ? 'kein freier Slot' : `custom_label_${slot}`;
}

/** Slots mit fremdem Merchant-Wert, höchster zuerst (Zählrichtung des Laufs). */
function occupiedSlots(feed: AdsLabelFeed): string[] {
  return feed.used_slots
    .map((count, index) => ({ count, index }))
    .filter((entry) => entry.count > 0)
    .map((entry) => `custom_label_${entry.index}`)
    .reverse();
}

/**
 * Der letzte Label-Lauf eines Feeds, als eigene Spalte.
 *
 * Es MUSS drei Zustaende geben, nicht zwei. Nach einem Label-Lauf sieht die
 * Vorschau daneben Zeichen fuer Zeichen so aus wie davor -- `used_slots` bleibt
 * konstruktionsbedingt auf lauter Nullen, weil unsere eigenen `fo_`-Werte dort
 * bewusst nicht als belegter Slot zaehlen. Diese Spalte ist damit die einzige
 * Stelle der Flaeche, an der ein Schreibzugriff nach aussen sichtbar wird; ein
 * falsches "noch nie gelabelt" waere hier schlimmer als gar keine Angabe.
 */
function LastLabelRunCell({ state }: { state: AdsLastLabelRunState }) {
  // Weder geladen noch fehlgeschlagen darf sich als Aussage ueber den Feed
  // ausgeben. Leer heisst leer.
  if (state.kind === 'unknown') return null;

  if (state.kind === 'never') {
    return <span className="text-xs text-muted-foreground">noch nie gelabelt</span>;
  }

  const { occurred_at, eligible_with_tier, chosen_slot } = state.run;
  const details = [
    eligible_with_tier === null ? null : `${int(eligible_with_tier)} Produkte`,
    chosen_slot === null ? null : slotText(chosen_slot),
  ].filter((part): part is string => part !== null);

  return (
    <div className="space-y-0.5">
      <p className="text-xs tabular-nums">
        {occurred_at ? dateTime(occurred_at) : 'gelabelt, Zeitpunkt unbekannt'}
      </p>
      {details.length > 0 && (
        <p className="text-xs text-muted-foreground tabular-nums">{details.join(' · ')}</p>
      )}
    </div>
  );
}

interface LabelRowProps {
  feed: AdsLabelFeed;
  accountLabel: string;
  blocked: LabelUploadBlock | null;
  pilotLabel: string | null;
  isBusy: boolean;
  lastRun: AdsLastLabelRunState;
  onUpload: (feedId: string) => void;
}

function LabelRow({
  feed,
  accountLabel,
  blocked,
  pilotLabel,
  isBusy,
  lastRun,
  onUpload,
}: LabelRowProps) {
  // Der Fall, den die Fläche benennen MUSS statt ihn zu verstecken: alles
  // optimiert, nichts freigegeben. Auf zwergehuus sind das 4.902 Produkte, und
  // ein bloßes "0" ohne Grund liest sich wie ein Defekt der Software.
  const blockedByApproval = feed.eligible === 0 && feed.awaiting_review > 0;

  return (
    <TableRow data-testid={`ads-label-feed-${feed.feed_id}`}>
      <TableCell>
        <div className="font-medium">{feed.mc_name ?? NO_VALUE}</div>
        <div className="text-xs text-muted-foreground">{feed.feed_name}</div>
      </TableCell>
      <TableCell>
        <span
          className={`rounded-md px-2 py-1 font-mono text-xs ${
            feed.chosen_slot === null
              ? 'bg-muted text-muted-foreground'
              : 'bg-primary-light text-primary-ink'
          }`}
        >
          {slotText(feed.chosen_slot)}
        </span>
        {/* Welche Slots ein fremder Merchant-Wert belegt. Sie werden nie
            überschrieben, und ohne diese Zeile bliebe unerklärlich, warum
            der Lauf nicht auf Slot 4 schreibt. */}
        {occupiedSlots(feed).length > 0 && (
          <div className="mt-1 text-[11px] text-muted-foreground tabular-nums">
            belegt: {occupiedSlots(feed).join(', ')}
          </div>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="font-semibold tabular-nums">{int(feed.eligible_with_tier)}</div>
        <div className="text-xs text-muted-foreground tabular-nums">
          von {int(feed.products_total)}
        </div>
        {/* Gehen mit hoch, bekommen aber kein Label -- sichtbar, damit die
            Label-Zahl nicht wie ein Zaehlfehler aussieht. */}
        {feed.unlabeled_disapproved > 0 && (
          <div className="text-[11px] text-muted-foreground tabular-nums">
            {int(feed.unlabeled_disapproved)} werden ohne Label hochgeladen (von Google nicht ausgeliefert)
          </div>
        )}
      </TableCell>
      <TableCell className="text-right">
        {feed.first_upload > 0 ? (
          <span className="font-semibold text-warning-ink tabular-nums">
            {int(feed.first_upload)}
          </span>
        ) : (
          <span className="text-muted-foreground tabular-nums">0</span>
        )}
      </TableCell>
      <TableCell className="text-right text-muted-foreground tabular-nums">
        {int(feed.awaiting_review)}
      </TableCell>
      <TableCell className="min-w-[190px]">
        <LastLabelRunCell state={lastRun} />
      </TableCell>
      <TableCell className="min-w-[240px]">
        {blocked ? (
          <div className="space-y-1.5">
            <Button size="sm" variant="outline" disabled>
              Labels hochladen
            </Button>
            <p className="text-xs text-muted-foreground">
              {blockedByApproval
                ? `0 labelbar, weil keine Freigabe: ${int(feed.awaiting_review)} Produkte warten in der Werkbank.`
                : blockText(blocked, pilotLabel)}
            </p>
          </div>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" disabled={isBusy}>
                {isBusy ? 'lädt hoch …' : 'Labels hochladen'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Labels zu Google hochladen?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <span className="block space-y-2 text-sm text-muted-foreground">
                    <span className="block">
                      {accountLabel} {' · '} {feed.feed_name}: {int(feed.eligible_with_tier)}{' '}
                      Produkte bekommen ihr Tier als {slotText(feed.chosen_slot)} in den
                      Supplemental Feed. Die Merchant-Originale bleiben unberührt.
                    </span>
                    {feed.unlabeled_disapproved > 0 && (
                      <span className="block">
                        Davon {int(feed.unlabeled_disapproved)} ohne Label: von Google für Shopping Ads
                        abgelehnt, Content und Attribute gehen trotzdem hoch.
                      </span>
                    )}
                    {feed.first_upload > 0 && (
                      <span className="block font-medium text-warning-ink">
                        Achtung: {int(feed.first_upload)} dieser Produkte waren noch nie bei
                        Google. Dieser Lauf ist dort zugleich der erste Content-Upload, nicht bloß
                        eine Label-Rotation.
                      </span>
                    )}
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={() => onUpload(feed.feed_id)}>
                  Hochladen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </TableCell>
    </TableRow>
  );
}

interface LabelSectionProps {
  account: AdsOverviewAccount;
  pilot: AdsPilotInfo | null;
  preview: AdsLabelPreview | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  busyFeedId: string | null;
  /**
   * Das Aenderungs-Log dieses Kontos, oder `undefined`, solange es nicht
   * vorliegt. Die Unterscheidung traegt: `lastLabelRunFor` macht daraus den
   * Zustand "wir wissen es nicht" statt "noch nie gelabelt".
   */
  labelHistory: AdsChangeLogEntry[] | undefined;
  onUpload: (feedId: string) => void;
}

function LabelSection({
  account,
  pilot,
  preview,
  isLoading,
  isError,
  onRetry,
  busyFeedId,
  labelHistory,
  onUpload,
}: LabelSectionProps) {
  const pilotLabel = pilot?.label ?? null;
  const firstUploadTotal = (preview?.feeds ?? []).reduce((sum, feed) => sum + feed.first_upload, 0);
  const accountBlock = labelUploadBlock(account, pilot, preview, null);
  // `no_preview` heißt nur "noch nicht geladen" und ist kein Sperrgrund, den
  // man dem Nutzer als Zustand des Kontos vorlegt.
  const showAccountBlock = accountBlock !== null && accountBlock !== 'no_preview';

  return (
    <section className="space-y-3" aria-labelledby="ads-label-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="ads-label-heading" className="text-lg font-semibold">
          Label-Upload
        </h2>
        <span className="text-xs text-muted-foreground">
          Je Feed ein Lauf. Der Slot wird pro Feed neu aufgelöst, nicht angenommen.
        </span>
      </div>

      {showAccountBlock && accountBlock && (
        <WarnBanner title={`${account.label} ist für Label-Läufe gesperrt`}>
          <p>{blockText(accountBlock, pilotLabel)}</p>
        </WarnBanner>
      )}

      {firstUploadTotal > 0 && (
        <WarnBanner title="Dieser Lauf ist zugleich ein Content-Rollout">
          <p>
            {int(firstUploadTotal)} der labelbaren Produkte waren noch nie bei Google. Für sie
            schiebt der Label-Lauf zum ersten Mal den vollständigen optimierten Inhalt live, nicht
            bloß ein Custom Label.
          </p>
        </WarnBanner>
      )}

      {!account.connected ? (
        // H6-F3: ohne OAuth-Zeile wird die Vorschau gar nicht abgefragt. Der
        // WarnBanner oben (`not_connected`) IST der Zustand dieser Flaeche --
        // ein Fehler- oder Leerzustand darunter wuerde ihm widersprechen
        // ("Keine Feeds im Scope" ist ohne Abfrage schlicht nicht gewusst).
        null
      ) : isError ? (
        <ErrorState
          description="Die Label-Vorschau konnte nicht geladen werden."
          onRetry={onRetry}
        />
      ) : isLoading ? (
        <Skeleton className="h-48 w-full rounded-lg" />
      ) : !preview || preview.feeds.length === 0 ? (
        <EmptyState
          icon={Tags}
          title="Keine Feeds im Scope dieses Kontos"
          description="Ein Feed gehört zum Scope, wenn er an der verlinkten Merchant Center hängt und für dieses Ads-Konto jemals Leistungszeilen hatte."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">Feed</TableHead>
                    <TableHead>Slot</TableHead>
                    <TableHead className="text-right">labelbar</TableHead>
                    <TableHead className="text-right">davon Erstupload</TableHead>
                    <TableHead className="text-right">wartet auf Freigabe</TableHead>
                    <TableHead className="min-w-[190px]">zuletzt gelabelt</TableHead>
                    <TableHead>Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.feeds.map((feed) => (
                    <LabelRow
                      key={feed.feed_id}
                      feed={feed}
                      accountLabel={account.label}
                      blocked={labelUploadBlock(account, pilot, preview, feed)}
                      pilotLabel={pilotLabel}
                      isBusy={busyFeedId === feed.feed_id}
                      lastRun={lastLabelRunFor(labelHistory, feed.feed_id)}
                      onUpload={onUpload}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs leading-relaxed text-muted-foreground">
        Ein Label erreicht ausschließlich Produkte, die optimiert UND freigegeben sind: es reist auf
        dem Produkt-Upload mit und ist kein eigener Schreibweg. Gezählt wird vom höchsten Slot
        abwärts; ein Slot mit fremdem Wert gilt als belegt und wird nie überschrieben, ein eigener
        Wert gilt als frei und wird ersetzt. Ist kein Slot frei, bekommt der Feed kein Label und
        keinen Fehler. Ein Produkt ohne gerechnetes Tier bekommt kein Label; geraten wird nie.
      </p>
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Abschnitt 4 -- Änderungs-Log
// ═════════════════════════════════════════════════════════════════════════════

const CLASSIFICATIONS: ReadonlyArray<{ value: AdsChangeClassification; label: string }> = [
  { value: 'gesperrt', label: 'gesperrt' },
  { value: 'protokollpflichtig', label: 'protokollpflichtig' },
  { value: 'frei', label: 'frei' },
];

const CLASSIFICATION_STYLE: Record<AdsChangeClassification, string> = {
  gesperrt: 'bg-destructive-light text-destructive-ink',
  protokollpflichtig: 'bg-warning-light text-warning-ink',
  frei: 'bg-muted text-muted-foreground',
};

function ChangeLogRow({ entry }: { entry: AdsChangeLogEntry }) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap text-xs tabular-nums">
        {dateTime(entry.occurred_at)}
      </TableCell>
      <TableCell className="text-sm">
        {entry.account_label ?? entry.ads_customer_id}
        <div className="font-mono text-[11px] text-muted-foreground tabular-nums">
          {entry.ads_customer_id}
        </div>
      </TableCell>
      <TableCell>
        <span
          className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${CLASSIFICATION_STYLE[entry.classification]}`}
        >
          {entry.classification}
        </span>
      </TableCell>
      <TableCell className="font-mono text-xs">{entry.action}</TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {entry.actor === 'human' ? 'von Hand' : 'Software'}
      </TableCell>
      <TableCell className="text-sm">{entry.summary}</TableCell>
    </TableRow>
  );
}

interface ChangeLogSectionProps {
  accounts: AdsOverviewAccount[];
  entries: AdsChangeLogEntry[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  defaultCustomerId: string | null;
  feeds: AdsLabelFeed[];
  feedsCustomerId: string | null;
  isSaving: boolean;
  onSubmit: (input: {
    ads_customer_id: string;
    classification: AdsChangeClassification;
    /** Eingriffsart. Heißt log_action, weil `action` die Modul-Aktion trägt. */
    log_action: string;
    summary: string;
    detail?: Record<string, unknown>;
    feed_id?: string;
    occurred_at?: string;
  }) => Promise<boolean>;
}

const NO_FEED = '__none__';

function ChangeLogSection({
  accounts,
  entries,
  isLoading,
  isError,
  onRetry,
  defaultCustomerId,
  feeds,
  feedsCustomerId,
  isSaving,
  onSubmit,
}: ChangeLogSectionProps) {
  /**
   * `null` heißt "der Nutzer hat noch nichts gewählt" -- dann folgt das
   * Formular der Auswahl der Tafel. Sobald er selbst wählt, bleibt seine Wahl
   * stehen, auch wenn oben ein anderes Konto angeklickt wird. Ein
   * `useState(defaultCustomerId)` hätte den Startwert genau einmal gelesen und
   * wäre danach von der Tafel abgekoppelt.
   */
  const [chosenCustomerId, setChosenCustomerId] = useState<string | null>(null);
  const customerId = chosenCustomerId ?? defaultCustomerId ?? '';
  const [classification, setClassification] =
    useState<AdsChangeClassification>('protokollpflichtig');
  const [action, setAction] = useState('');
  const [summary, setSummary] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [feedId, setFeedId] = useState<string>(NO_FEED);
  const [detail, setDetail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Die Feed-Auswahl steht nur, wenn die geladene Vorschau zu DIESEM Konto
  // gehört. Eine Liste fremder Feeds wäre schlimmer als gar keine.
  const feedChoices = feedsCustomerId && feedsCustomerId === customerId ? feeds : [];

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    if (!customerId) {
      setFormError('Bitte ein Konto wählen.');
      return;
    }
    if (action.trim().length === 0) {
      setFormError('Die Handlung braucht eine Kennung, z.B. budget_change.');
      return;
    }
    if (summary.trim().length === 0) {
      setFormError('Ohne Zusammenfassung ist der Eintrag kein Nachweis.');
      return;
    }

    let parsedDetail: Record<string, unknown> | undefined;
    if (detail.trim().length > 0) {
      try {
        const parsed: unknown = JSON.parse(detail);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          setFormError('Detail muss ein JSON-Objekt sein, z.B. {"von": 3.0, "auf": 3.4}.');
          return;
        }
        parsedDetail = parsed as Record<string, unknown>;
      } catch {
        setFormError('Detail ist kein gültiges JSON.');
        return;
      }
    }

    let isoOccurredAt: string | undefined;
    if (occurredAt.length > 0) {
      const parsed = new Date(occurredAt);
      if (Number.isNaN(parsed.getTime())) {
        setFormError('Der Zeitpunkt ist unlesbar.');
        return;
      }
      isoOccurredAt = parsed.toISOString();
    }

    const ok = await onSubmit({
      ads_customer_id: customerId,
      classification,
      log_action: action.trim(),
      summary: summary.trim(),
      ...(parsedDetail ? { detail: parsedDetail } : {}),
      ...(feedId !== NO_FEED ? { feed_id: feedId } : {}),
      ...(isoOccurredAt ? { occurred_at: isoOccurredAt } : {}),
    });

    if (ok) {
      setAction('');
      setSummary('');
      setDetail('');
      setOccurredAt('');
      setFeedId(NO_FEED);
    }
  };

  return (
    <section className="space-y-3" aria-labelledby="ads-log-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="ads-log-heading" className="text-lg font-semibold">
          Änderungs-Log
        </h2>
        <span className="text-xs text-muted-foreground">
          Der Nachweis entsteht nicht dadurch, dass nichts geändert wurde, sondern dadurch, dass es
          belegbar ist.
        </span>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_340px] lg:items-start">
        <Card>
          <CardContent className="p-0">
            {isError ? (
              <div className="p-4">
                <ErrorState
                  description="Das Änderungs-Log konnte nicht geladen werden."
                  onRetry={onRetry}
                />
              </div>
            ) : isLoading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-10 w-full rounded-md" />
                ))}
              </div>
            ) : entries.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                Noch kein Eintrag. Das heißt: kein protokollierter Eingriff, nicht "keine Daten".
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Zeitpunkt</TableHead>
                      <TableHead>Konto</TableHead>
                      <TableHead>Einstufung</TableHead>
                      <TableHead>Handlung</TableHead>
                      <TableHead>Urheber</TableHead>
                      <TableHead className="min-w-[220px]">Zusammenfassung</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <ChangeLogRow key={entry.id} entry={entry} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-primary" />
              Eintrag von Hand
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Für Eingriffe außerhalb dieser Fläche, etwa eine Budget- oder tROAS-Änderung im
              Ads-Konto. Die Software protokolliert ihre eigenen Schritte selbst; dieser Weg trägt
              immer den Urheber "von Hand".
            </p>
          </CardHeader>
          <CardContent>
            <form className="space-y-3" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <Label htmlFor="ads-log-account">Konto</Label>
                <Select value={customerId} onValueChange={setChosenCustomerId}>
                  <SelectTrigger id="ads-log-account">
                    <SelectValue placeholder="Konto wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((account) => (
                      <SelectItem key={account.ads_customer_id} value={account.ads_customer_id}>
                        {account.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ads-log-classification">Einstufung</Label>
                <Select
                  value={classification}
                  onValueChange={(value) => setClassification(value as AdsChangeClassification)}
                >
                  <SelectTrigger id="ads-log-classification">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATIONS.map((entry) => (
                      <SelectItem key={entry.value} value={entry.value}>
                        {entry.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ads-log-action">Handlung</Label>
                <Input
                  id="ads-log-action"
                  value={action}
                  onChange={(event) => setAction(event.target.value)}
                  placeholder="budget_change"
                  list="ads-log-action-suggestions"
                />
                <datalist id="ads-log-action-suggestions">
                  <option value="budget_change" />
                  <option value="troas_change" />
                  <option value="campaign_structure" />
                  <option value="feed_change" />
                  <option value="tier_apply" />
                  <option value="label_upload" />
                </datalist>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ads-log-summary">Zusammenfassung</Label>
                <Textarea
                  id="ads-log-summary"
                  value={summary}
                  onChange={(event) => setSummary(event.target.value)}
                  rows={3}
                  placeholder="Was wurde geändert, und warum?"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ads-log-occurred">Zeitpunkt (optional)</Label>
                <Input
                  id="ads-log-occurred"
                  type="datetime-local"
                  value={occurredAt}
                  onChange={(event) => setOccurredAt(event.target.value)}
                />
                <p className="text-[11px] text-muted-foreground">
                  Leer lassen heißt jetzt. Für einen nachgetragenen Eingriff den echten Zeitpunkt
                  setzen.
                </p>
              </div>

              {feedChoices.length > 0 && (
                <div className="space-y-1.5">
                  <Label htmlFor="ads-log-feed">Feed (optional)</Label>
                  <Select value={feedId} onValueChange={setFeedId}>
                    <SelectTrigger id="ads-log-feed">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_FEED}>kein Feed-Bezug</SelectItem>
                      {feedChoices.map((feed) => (
                        <SelectItem key={feed.feed_id} value={feed.feed_id}>
                          {feed.feed_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="ads-log-detail">Detail als JSON (optional)</Label>
                <Textarea
                  id="ads-log-detail"
                  value={detail}
                  onChange={(event) => setDetail(event.target.value)}
                  rows={2}
                  className="font-mono text-xs"
                  placeholder='{"von": 3.0, "auf": 3.4}'
                />
              </div>

              {formError && <p className="text-xs font-medium text-destructive-ink">{formError}</p>}

              <Button type="submit" className="w-full" disabled={isSaving}>
                {isSaving ? 'speichert …' : 'Eintrag speichern'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Seite
// ═════════════════════════════════════════════════════════════════════════════

const AdsSteuerung: React.FC = () => {
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState(28);
  /**
   * Die ANGEZEIGTEN Trockenläufe dieser Sitzung, je Konto.
   *
   * Bewusst React-State und kein Query-Cache: "Übernehmen" darf nur, wer den
   * Lauf gerade vor sich hat. Ein Neuladen der Seite löscht sie, und danach
   * muss erst wieder gerechnet werden. Genau das ist die harte Grenze.
   */
  const [shownRuns, setShownRuns] = useState<Record<string, AdsDryRunResult>>({});
  const [busyFeedId, setBusyFeedId] = useState<string | null>(null);

  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewError,
    refetch: refetchOverview,
    isFetching: overviewFetching,
  } = useAdsOverview();

  const accounts = overview?.accounts ?? [];
  const pilot = overview?.pilot ?? null;

  // Vorauswahl: der Pilot. Er geht den anderen drei ohnehin voraus, und ohne
  // Vorauswahl wären die Abschnitte 2 und 3 beim ersten Laden leer.
  const effectiveCustomerId =
    selectedCustomerId ??
    accounts.find((account) => account.is_pilot)?.ads_customer_id ??
    accounts.find((account) => account.role === 'rollout')?.ads_customer_id ??
    null;

  const selectedAccount =
    accounts.find((account) => account.ads_customer_id === effectiveCustomerId) ?? null;

  const dryRunMutation = useAdsDryRun();
  const applyMutation = useAdsTierApply();
  const uploadMutation = useAdsLabelUpload();
  const changeLogAdd = useAdsChangeLogAdd();
  const rotationPause = useAdsRotationPause();

  const handleRotationPause = async (customerId: string, paused: boolean) => {
    try {
      const result = (await rotationPause.mutateAsync({
        ads_customer_id: customerId, paused,
      })) as { logged?: boolean } | null;
      // Das Änderungs-Log ist das Prüfprotokoll -- ein Veto ohne Eintrag
      // ist zur Auswertungszeit unsichtbar. Der Schalter steht trotzdem
      // (die Function schreibt ihn vor dem Log), also warnen, nicht
      // zurückrollen.
      if (result && result.logged === false) {
        toast.warning('Schalter gesetzt, aber NICHT protokolliert', {
          description: 'Der Änderungs-Log-Eintrag ist fehlgeschlagen. Bitte den Eingriff '
            + 'von Hand im Protokoll-Formular nachtragen.',
          duration: 20000,
        });
      }
      toast.success(paused ? 'Rotation pausiert' : 'Rotation fortgesetzt', {
        description: paused
          ? 'Der Wochenlauf rechnet weiter, lädt aber keine Labels mehr hoch. Eine stehende '
            + 'Ankündigung wird verworfen.'
          // Nicht 'ab Montag': steht für heute noch eine fällige Ankündigung,
          // lädt der Wochenlauf schon HEUTE 15:30 wieder hoch (Review-Befund).
          : 'Die nächste fällige Ankündigung wird wieder hochgeladen -- steht heute noch eine, '
            + 'bereits heute um 15:30 UTC.',
      });
    } catch (error) {
      toast.error('Schalter nicht gesetzt', { description: errorText(error) });
    }
  };

  const {
    data: preview,
    isLoading: previewLoading,
    isError: previewError,
    refetch: refetchPreview,
    // H6-F3: fuer ein Konto ohne eigene OAuth-Zeile antwortet die Function
    // deterministisch mit 403 ACCOUNT_NOT_AVAILABLE -- die Abfrage gar nicht
    // erst stellen. Der Sperrzustand steht laengst in der LabelSection
    // (`not_connected`), ein generischer Fehler darueber widersprach ihm.
  } = useAdsLabelPreview(effectiveCustomerId, {
    enabled: selectedAccount?.connected === true,
  });

  const {
    data: changeLog,
    isLoading: logLoading,
    isError: logError,
    refetch: refetchLog,
  } = useAdsChangeLog();

  const guardrailsQuery = useAdsGuardrails(effectiveCustomerId);


  const shownRun = effectiveCustomerId ? (shownRuns[effectiveCustomerId] ?? null) : null;
  const applyBlocked = tierApplyBlock(selectedAccount, pilot, shownRun?.run ?? null);
  const labelBlockedForAccount = labelUploadBlock(selectedAccount, pilot, preview ?? null, null);

  const handleDryRun = async (customerId: string) => {
    setSelectedCustomerId(customerId);
    try {
      const result = await dryRunMutation.mutateAsync({
        ads_customer_id: customerId,
        window_days: windowDays,
      });
      setShownRuns((previous) => ({ ...previous, [customerId]: result }));
      toast.success('Trockenlauf gerechnet', {
        description: `${int(result.run.n_changes)} Produkte mit neuem Tier.`,
      });
    } catch (error) {
      toast.error('Trockenlauf fehlgeschlagen', { description: errorText(error) });
    }
  };

  const handleApply = async () => {
    if (!effectiveCustomerId || !shownRun) return;
    try {
      await applyMutation.mutateAsync({ run_id: shownRun.run.id });
      // Der angezeigte Lauf ist danach kein Trockenlauf mehr. Ohne diese Zeile
      // bliebe der Knopf bedienbar und ein zweiter Klick liefe in einen 409.
      setShownRuns((previous) => ({
        ...previous,
        [effectiveCustomerId]: {
          ...shownRun,
          run: { ...shownRun.run, status: 'applied' },
        },
      }));
      toast.success('Tier-Wechsel übernommen', {
        description: `${int(shownRun.run.n_immediate + shownRun.run.n_confirmed)} Produkte tragen jetzt ihr neues Tier.`,
      });
    } catch (error) {
      // "Abgelehnt" stimmt nur, solange nichts geschrieben wurde -- also für
      // die Vorbedingungen (APPLY_REJECTED, BLOCKED, LOGIC_FROZEN, ...). Bei
      // den beiden Abbruch-Codes trägt ein Teil der Produkte bereits sein
      // neues Tier; eine Überschrift, die das Gegenteil behauptet, liest der
      // Bediener zuerst und den richtigen Fließtext darunter vielleicht gar
      // nicht. Wortgleich mit dem Namen, unter dem der Lauf ab jetzt an der
      // Kontokarte steht: derselbe Zustand soll nicht zwei Namen haben.
      const code = adsErrorCode(error);
      const partiallyWritten = code === 'APPLY_FAILED' || code === 'APPLY_INCOMPLETE';
      toast.error(partiallyWritten ? 'Übernahme unvollständig' : 'Übernahme abgelehnt', {
        description: errorText(error),
        // Der einzige Ausgang dieser Fläche, der einen Menschen braucht, darf
        // nicht nach vier Sekunden weg sein (Muster: der nicht protokollierte
        // Label-Lauf weiter unten).
        duration: partiallyWritten ? 20000 : undefined,
      });
    }
  };

  const handleUpload = async (feedId: string) => {
    if (!effectiveCustomerId) return;
    setBusyFeedId(feedId);
    try {
      const result = await uploadMutation.mutateAsync({
        ads_customer_id: effectiveCustomerId,
        feed_id: feedId,
      });
      // Der Upload läuft zu diesem Zeitpunkt bereits; ein gescheiterter
      // Protokolleintrag macht ihn nicht rückgängig. Er darf aber auch nicht
      // als Erfolg durchgehen: das Änderungs-Log IST der Nachweis, und eine
      // Lücke darin fällt sonst erst bei der Auswertung Ende Oktober auf --
      // dann, wenn sie nicht mehr zu schließen ist.
      if (!result.logged) {
        toast.warning('Label-Lauf gestartet, aber NICHT protokolliert', {
          description:
            'Der Upload läuft, der Eintrag ins Änderungs-Log ist gescheitert. '
            + 'Bitte von Hand nachtragen. '
            + String(result.log_error ?? ''),
          duration: 20000,
        });
      } else {
        toast.success('Label-Lauf gestartet', {
          description: 'Der Upload läuft über die normale Upload-Warteschlange.',
        });
      }
    } catch (error) {
      toast.error('Label-Lauf abgelehnt', { description: errorText(error) });
    } finally {
      setBusyFeedId(null);
    }
  };

  const handleChangeLogAdd = async (
    input: Parameters<ChangeLogSectionProps['onSubmit']>[0],
  ): Promise<boolean> => {
    try {
      await changeLogAdd.mutateAsync(input);
      toast.success('Eintrag gespeichert');
      return true;
    } catch (error) {
      toast.error('Eintrag nicht gespeichert', { description: errorText(error) });
      return false;
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        title="Ads-Steuerung"
        subtitle="Vier Rollout-Konten und eine Negativkontrolle. Jede Schreibaktion läuft erst als Trockenlauf, und nichts an dieser Fläche startet oder pausiert eine Kampagne."
        actions={
          <div className="flex items-center gap-3">
            {/* Das Sperrfenster steht auch dann da, wenn es noch nicht läuft:
                "ab wann friert die Logik ein" ist die Frage, die man VOR dem
                Stichtag beantwortet haben will, nicht danach. */}
            {overview?.freeze && !overview.freeze.active && overview.freeze.from && (
              <span className="hidden text-xs text-muted-foreground sm:block">
                Messfenster {dateOnly(overview.freeze.from)} bis {dateOnly(overview.freeze.to)}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchOverview()}
              disabled={overviewFetching}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${overviewFetching ? 'animate-spin' : ''}`} />
              Neu laden
            </Button>
          </div>
        }
      />

      {overview?.freeze?.active && (
        <WarnBanner title="Sperrfenster aktiv: die Logik ist eingefroren">
          <p>
            Vom {dateOnly(overview.freeze.from)} bis {dateOnly(overview.freeze.to)} dürfen weder
            Schwellen noch Logik-Version abweichen. Ein Lauf mit abweichenden Werten wird
            serverseitig abgelehnt.
          </p>
        </WarnBanner>
      )}

      {overviewLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      ) : overviewError ? (
        <ErrorState
          description="Die Rollout-Tafel konnte nicht geladen werden."
          onRetry={() => refetchOverview()}
        />
      ) : !overview ? (
        <EmptyState
          icon={CircleSlash}
          title="Keine Daten"
          description="Die Function hat nichts zurückgegeben."
        />
      ) : (
        <>
          <RolloutBoard
            overview={overview}
            selectedCustomerId={effectiveCustomerId}
            isBusy={dryRunMutation.isPending}
            rotationBusy={rotationPause.isPending}
            onSelect={setSelectedCustomerId}
            onDryRun={handleDryRun}
            onRotationPause={handleRotationPause}
          />

          {selectedAccount && (
            <>
              <section className="space-y-3" aria-labelledby="ads-flow-heading">
                <h2 id="ads-flow-heading" className="text-lg font-semibold">
                  Ablauf für {selectedAccount.label}
                </h2>
                <StepStrip
                  account={selectedAccount}
                  shownRun={shownRun?.run ?? null}
                  applyBlocked={applyBlocked}
                  labelBlocked={
                    labelBlockedForAccount === 'no_preview' ? null : labelBlockedForAccount
                  }
                  pilotLabel={pilot?.label ?? null}
                />
              </section>

              <GuardrailBoard
                report={guardrailsQuery.data}
                isLoading={guardrailsQuery.isLoading}
                error={guardrailsQuery.error}
                onRetry={() => void guardrailsQuery.refetch()}
                accountLabel={selectedAccount.label}
                isNegativeControl={selectedAccount.role === 'negative_control'}
              />

              <TieringSection
                account={selectedAccount}
                pilot={pilot}
                dryRun={shownRun}
                windowDays={windowDays}
                onWindowDaysChange={setWindowDays}
                onDryRun={() => handleDryRun(selectedAccount.ads_customer_id)}
                onApply={handleApply}
                isRunning={dryRunMutation.isPending}
                isApplying={applyMutation.isPending}
                applyBlocked={applyBlocked}
              />

              <LabelSection
                account={selectedAccount}
                pilot={pilot}
                preview={preview ?? null}
                isLoading={previewLoading}
                isError={previewError}
                onRetry={() => refetchPreview()}
                busyFeedId={busyFeedId}
                labelHistory={changeLog}
                onUpload={handleUpload}
              />

              <AdsStructureSection
                customerId={selectedAccount.ads_customer_id}
                accountLabel={selectedAccount.label}
              />
            </>
          )}

          <ChangeLogSection
            accounts={accounts}
            entries={changeLog ?? []}
            isLoading={logLoading}
            isError={logError}
            onRetry={() => refetchLog()}
            defaultCustomerId={effectiveCustomerId}
            feeds={preview?.feeds ?? []}
            feedsCustomerId={effectiveCustomerId}
            isSaving={changeLogAdd.isPending}
            onSubmit={handleChangeLogAdd}
          />
        </>
      )}
    </div>
  );
};

export default AdsSteuerung;
